/**
 * MultiTenancyPageV2 — V191 / Phase-5 redesign of Administer → Deployments.
 *
 * Hierarchy (per design doc §1):
 *
 *     Org "Acme Corp"
 *     ├─ Tenant "Acme Healthcare"
 *     │  ├─ Instance "Dev"
 *     │  ├─ Instance "Staging"
 *     │  └─ Instance "Production"
 *     ├─ Tenant "Acme Finance"
 *     │  └─ Instance "Default"
 *     └─ …
 *
 * Layout:
 *   ┌─────────────┬──────────────────────────────────────────────┐
 *   │ TenancyTree │  DetailPanel (Org / Tenant / Instance tabs)  │
 *   │  (left)     │  (right — tabs: Overview, Members, Resources)│
 *   └─────────────┴──────────────────────────────────────────────┘
 *
 * Read-only this slice. Create / edit / archive / promote flows land in
 * Phase 9 (writers) and Phase 11 (Promotion Graph editor). Phase 5 wires
 * everything to /api/v1/instances + /api/v1/tenants + /api/v1/organizations.
 *
 * The /deployments path soft-redirects to /multi-tenancy. The V1
 * Deployments page is no longer reachable through user-facing nav as of
 * V195 — the redesign is the canonical surface.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Building2, Layers, Boxes, RefreshCw, ChevronDown, ChevronRight,
  Shield, Activity, Server, Users as UsersIcon, Plus, Edit2, Trash2,
  Copy, Check,
} from 'lucide-react';
import {
  Button, Pill, Card, EmptyState, TabNav, MetricCard, Slideout, Modal,
} from '../components/v2';
import { usePlatform } from '../contexts/PlatformContext';
import EntityLabel from '../components/common/EntityLabel';
import { useAuth } from '../contexts/AuthContext';
/* Shared scope-side member management — the same add/remove used to fix the
   "no way to reassign the admin after creation" gap on every scope. */
import ScopeMemberManager from '../components/v2/ScopeMemberManager';
import { useToast } from '../contexts/ToastContext';
import * as api from '../services/api';

// ─── Tier → pill tone mapping (mirrors V192 backfill canonical strings) ───
const TIER_TONE = {
  PRODUCTION: 'danger',   // red — production gets attention
  STAGING:    'warning',
  DEV:        'cyan',
  SYSTEM:     'muted',
  CUSTOM:     'muted',
};

const STATUS_TONE = {
  ACTIVE:   'success',
  PAUSED:   'warning',
  ARCHIVED: 'muted',
};

// ──────────────────────────────────────────────────────────────────────
// TenancyTree — left-rail nested Org → Tenant → Instance.
// Selection callback yields {kind, orgId, tenantId, instanceId}; the
// detail panel resolves the right row from those ids.
// ──────────────────────────────────────────────────────────────────────
function TenancyTree({
  customers: customersProp, orgs: orgsProp, tenants: tenantsProp, instancesByTenantId,
  selected, onSelect,
}) {
  // Defensive — PlatformContext can transiently yield undefined arrays
  // during initial load even though the page-level destructure has
  // = [] defaults (some loaders set state via setState(undefined) on
  // failure paths). Guard here so the tree renders an empty shell
  // instead of crashing the AppErrorBoundary.
  const customers = Array.isArray(customersProp) ? customersProp : [];
  const orgs = Array.isArray(orgsProp) ? orgsProp : [];
  const tenants = Array.isArray(tenantsProp) ? tenantsProp : [];
  // Default-expand state. Customers default-expanded so the tree looks
  // similar to today's flat-org view; operator can collapse to compact.
  const [expandedCustomers, setExpandedCustomers] = useState(() => new Set());
  const [expandedOrgs, setExpandedOrgs] = useState(() => new Set());
  const [expandedTenants, setExpandedTenants] = useState(() => new Set());

  /* V208 grouping — bucket orgs by customer_id, plus an "Unassigned"
   * catch-all for any org with NULL customer_id (shouldn't happen
   * post-V208 since the migration assertively backfills, but defensive).
   *
   * Customer ordering: ACTIVE first by displayName, then SUSPENDED, then
   * OFFBOARDED at the bottom — matches the operator's mental priority. */
  const customerGroups = useMemo(() => {
    const customersById = new Map(customers.map((c) => [c.id, c]));
    const buckets = new Map();
    customers.forEach((c) => buckets.set(c.id, { customer: c, orgs: [] }));
    const unassigned = { customer: null, orgs: [] };
    orgs.forEach((o) => {
      const cid = o.customerId || o.customer_id || null;
      if (cid && buckets.has(cid)) {
        buckets.get(cid).orgs.push(o);
      } else if (cid && customersById.has(cid)) {
        buckets.set(cid, { customer: customersById.get(cid), orgs: [o] });
      } else {
        unassigned.orgs.push(o);
      }
    });
    const rankStatus = (s) => (s === 'ACTIVE' ? 0 : s === 'SUSPENDED' ? 1 : s === 'OFFBOARDED' ? 2 : 1);
    const sorted = Array.from(buckets.values()).sort((a, b) => {
      const sa = rankStatus(a.customer?.status);
      const sb = rankStatus(b.customer?.status);
      if (sa !== sb) return sa - sb;
      const na = a.customer?.displayName || a.customer?.name || '';
      const nb = b.customer?.displayName || b.customer?.name || '';
      return na.localeCompare(nb);
    });
    // Sort orgs within each customer alphabetically
    sorted.forEach((b) => b.orgs.sort((a, b2) => (a.name || '').localeCompare(b2.name || '')));
    unassigned.orgs.sort((a, b2) => (a.name || '').localeCompare(b2.name || ''));
    if (unassigned.orgs.length > 0) sorted.push(unassigned);
    return sorted;
  }, [customers, orgs]);

  // Auto-expand all customers on first load (mirrors today's flat-tree feel).
  // Subsequent renders preserve operator's collapse state.
  const initialExpansionDone = React.useRef(false);
  useEffect(() => {
    if (initialExpansionDone.current) return;
    if (customerGroups.length === 0) return;
    setExpandedCustomers(new Set(
      customerGroups.map((g) => g.customer ? g.customer.id : '__unassigned__')
    ));
    initialExpansionDone.current = true;
  }, [customerGroups]);

  useEffect(() => {
    if (!selected) return;
    setExpandedOrgs(prev => {
      const next = new Set(prev);
      if (selected.orgId) next.add(selected.orgId);
      return next;
    });
    setExpandedTenants(prev => {
      const next = new Set(prev);
      if (selected.tenantId) next.add(selected.tenantId);
      return next;
    });
    // Also expand the customer that owns the selected org.
    if (selected.orgId) {
      const owningOrg = orgs.find((o) => o.id === selected.orgId);
      const cid = owningOrg && (owningOrg.customerId || owningOrg.customer_id);
      if (cid) {
        setExpandedCustomers((prev) => {
          const next = new Set(prev);
          next.add(cid);
          return next;
        });
      }
    }
  }, [selected, orgs]);

  const toggleCustomer = (id) => setExpandedCustomers(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleOrg = (id) => setExpandedOrgs(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleTenant = (id) => setExpandedTenants(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const isSelected = (kind, id) => {
    if (!selected) return false;
    if (kind === 'org') return selected.kind === 'org' && selected.orgId === id;
    if (kind === 'tenant') return selected.kind === 'tenant' && selected.tenantId === id;
    if (kind === 'instance') return selected.kind === 'instance' && selected.instanceId === id;
    return false;
  };

  return (
    <nav
      className="border border-v2-line rounded-v2-md bg-white overflow-hidden"
      style={{ minHeight: 460, maxHeight: 720, overflowY: 'auto' }}
      aria-label="Org / Tenant / Instance tree"
    >
      <div className="px-3 py-2 text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted border-b border-v2-line bg-v2-stone-50">
        Multi-Tenancy
      </div>

      <ul className="p-1 text-[12px]">
        {customerGroups.map((group) => {
          const customerKey = group.customer ? group.customer.id : '__unassigned__';
          const customerOpen = expandedCustomers.has(customerKey);
          const customerName = group.customer
            ? (group.customer.displayName || group.customer.name || group.customer.id)
            : 'Unassigned';
          const customerStatus = group.customer?.status;
          // Subtle muted treatment for non-ACTIVE customers so the operator
          // can scan to ACTIVE first; legacy backfill customer is also
          // dimmed to signal it's a bookkeeping bucket, not a real
          // business relationship.
          const customerDim = customerStatus && customerStatus !== 'ACTIVE';
          const isLegacy = group.customer?.name === 'legacy';
          return (
            <li key={customerKey} className="mb-1">
              {/* Customer header — collapsible group. Clicking the chevron
                * toggles only this group; the header itself is non-
                * selectable today (customer-detail panel is a Phase 2
                * follow-up — would surface billing, org count, audit). */}
              <button
                className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-v2-sm text-left
                  bg-v2-stone-50 hover:bg-v2-stone-100 border-l-2 ${
                    isLegacy ? 'border-v2-stone-300' : 'border-v2-action'
                  } ${customerDim ? 'opacity-65' : ''}`}
                onClick={() => toggleCustomer(customerKey)}
                title={group.customer ? `Customer: ${customerName}` : 'Orgs without a customer assignment'}
              >
                {customerOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                <UsersIcon size={13} className="text-v2-ink-muted" />
                <span className="truncate flex-1 text-[11.5px] font-semibold uppercase tracking-wider text-v2-ink">
                  {customerName}
                </span>
                {customerStatus && customerStatus !== 'ACTIVE' && (
                  <Pill tone={customerStatus === 'OFFBOARDED' ? 'muted' : 'warning'} size="sm">
                    {customerStatus.toLowerCase()}
                  </Pill>
                )}
                <span className="text-[10px] text-v2-ink-muted font-mono-v2">{group.orgs.length}</span>
              </button>

              {customerOpen && group.orgs.length === 0 && (
                <ul className="ml-3 mt-0.5 border-l border-v2-line pl-1">
                  <li className="px-2 py-1 text-[11px] text-v2-ink-muted italic">
                    No orgs under this customer
                  </li>
                </ul>
              )}

              {customerOpen && group.orgs.map(org => {
                const orgOpen = expandedOrgs.has(org.id);
                const orgTenants = tenants.filter(t => t.orgId === org.id);
                return (
                  <div key={org.id} className="ml-3 mt-0.5 border-l border-v2-line pl-1">
                    <button
                      className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-v2-sm text-left
                        ${isSelected('org', org.id) ? 'bg-v2-cyan-soft text-v2-cyan font-semibold' : 'hover:bg-v2-stone-100'}`}
                      onClick={() => { toggleOrg(org.id); onSelect({ kind: 'org', orgId: org.id }); }}
                    >
                      {orgOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      <Building2 size={13} className="text-v2-ink-muted" />
                      <span className="truncate flex-1">{org.name || org.id}</span>
                      <span className="text-[10px] text-v2-ink-muted">{orgTenants.length}</span>
                    </button>

              {orgOpen && (
                <ul className="ml-3 mt-0.5 border-l border-v2-line pl-1">
                  {orgTenants.length === 0 ? (
                    <li className="px-2 py-1 text-[11px] text-v2-ink-muted italic">No tenants</li>
                  ) : orgTenants.map(t => {
                    const tOpen = expandedTenants.has(t.id);
                    const tInstances = instancesByTenantId[t.id] || [];
                    return (
                      <li key={t.id}>
                        <button
                          className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-v2-sm text-left
                            ${isSelected('tenant', t.id) ? 'bg-v2-cyan-soft text-v2-cyan font-semibold' : 'hover:bg-v2-stone-100'}`}
                          onClick={() => { toggleTenant(t.id); onSelect({ kind: 'tenant', orgId: t.orgId, tenantId: t.id }); }}
                        >
                          {tOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          <Layers size={13} className="text-v2-ink-muted" />
                          <span className="truncate flex-1">{t.name || t.id}</span>
                          <span className="text-[10px] text-v2-ink-muted">{tInstances.length}</span>
                        </button>

                        {tOpen && (
                          <ul className="ml-3 mt-0.5 border-l border-v2-line pl-1">
                            {tInstances.length === 0 ? (
                              <li className="px-2 py-1 text-[11px] text-v2-ink-muted italic">No instances</li>
                            ) : tInstances.map(i => (
                              <li key={i.id}>
                                <button
                                  className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-v2-sm text-left
                                    ${isSelected('instance', i.id) ? 'bg-v2-cyan-soft text-v2-cyan font-semibold' : 'hover:bg-v2-stone-100'}`}
                                  onClick={() => onSelect({ kind: 'instance', orgId: t.orgId, tenantId: t.id, instanceId: i.id })}
                                  title={`${i.name} (${i.tier})`}
                                >
                                  <Boxes size={13} className="text-v2-ink-muted" />
                                  <span className="truncate flex-1">{i.name}</span>
                                  <Pill tone={TIER_TONE[i.tier] || 'muted'} size="sm">{i.tier}</Pill>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
                  </div>
                );
              })}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

// ──────────────────────────────────────────────────────────────────────
// G532 — MembersList: reusable table for the Org / Tenant / Instance
// Members tabs. Backed by /api/v1/{kind}/{id}/members → ScopeMemberDTO[].
// ──────────────────────────────────────────────────────────────────────
const ROLE_TONE_FOR_MEMBER = {
  'Super Admin':    'danger',
  'Platform Admin': 'danger',
  'Org Admin':      'warning',
  'Tenant Admin':   'warning',
  'Instance Admin': 'cyan',
  'Promoter':       'cyan',
  'Developer':      'muted',
  'Viewer':         'muted',
};

const STATUS_TONE_FOR_MEMBER = {
  active:   'success',
  invited:  'warning',
  inactive: 'muted',
  orphaned: 'danger',
};

function MembersList({ kind, scopeId, scopeHint }) {
  // G544 — Take stable (kind, scopeId) props instead of a useCallback loader.
  // Earlier API used `loader={useCallback(...)}` at the call site, but the
  // call site was inside a conditional `{tab === 'members' && <MembersList .../>}`
  // — that's a Rules-of-Hooks violation. By taking primitive props and
  // dispatching internally, every hook in this component now runs
  // unconditionally on every render.
  const scopeNoun = kind === 'org' ? 'organization'
                  : kind === 'tenant' ? 'tenant'
                  : kind === 'instance' ? 'instance'
                  : 'scope';
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!scopeId) return;
    let cancelled = false;
    setLoading(true); setError(null);
    (async () => {
      try {
        const fn = kind === 'org' ? api.getOrgMembers
                 : kind === 'tenant' ? api.getTenantMembers
                 : kind === 'instance' ? api.getInstanceMembers
                 : null;
        if (!fn) { setError(`Unknown scope kind: ${kind}`); setRows([]); return; }
        const res = await fn(scopeId);
        if (cancelled) return;
        if (res && res.ok === false) {
          setError(res.message || res.error || `Failed to load ${scopeNoun} members`);
          setRows([]);
        } else {
          const payload = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
          setRows(Array.isArray(payload) ? payload : []);
        }
      } catch (e) {
        if (!cancelled) { setError(e?.message || String(e)); setRows([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kind, scopeId, scopeNoun]);

  if (loading) {
    return <Card><Card.Body><div className="text-v2-ink-muted text-[12px]">Loading members…</div></Card.Body></Card>;
  }
  if (error) {
    return (
      <Card><Card.Body>
        <div className="text-[12px] text-v2-danger border border-v2-danger rounded-v2-md px-3 py-2 bg-v2-danger-soft">{error}</div>
      </Card.Body></Card>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        icon={<UsersIcon size={20} />}
        title={`No ${scopeNoun} members yet`}
        description={scopeHint || `Users with grants on this ${scopeNoun} will appear here once an administrator assigns them.`}
      />
    );
  }

  return (
    <Card>
      <Card.Body>
        <table className="w-full text-[12px]">
          <thead className="text-left text-v2-ink-muted text-[10.5px] uppercase tracking-wider">
            <tr>
              <th className="py-2">User</th>
              <th>Role</th>
              <th>Pinned scope</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-v2-line">
            {rows.map(m => {
              const pin = m.instanceId
                ? 'Instance'
                : m.tenantId
                  ? 'Tenant'
                  : m.orgId
                    ? 'Organization'
                    : '—';
              const statusKey = (m.status || 'active').toLowerCase();
              return (
                <tr key={m.membershipId || m.userId}>
                  <td className="py-2">
                    <div className="font-medium text-v2-ink">{m.name || '(unnamed)'}</div>
                    <div className="text-[10.5px] text-v2-ink-muted">{m.email}</div>
                  </td>
                  <td>
                    <Pill tone={ROLE_TONE_FOR_MEMBER[m.role] || 'muted'} size="sm">{m.role}</Pill>
                    {m.isDefault && (
                      <span className="ml-2 text-[10px] text-v2-ink-muted italic">default</span>
                    )}
                  </td>
                  <td className="text-v2-ink-muted">{pin}</td>
                  <td>
                    <Pill tone={STATUS_TONE_FOR_MEMBER[statusKey] || 'muted'} size="sm">{statusKey}</Pill>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card.Body>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// G540 — InstanceResourcesView: counts + small sample of every resource
// kind pinned to the instance. Backed by /api/v1/instances/{id}/resources.
// ──────────────────────────────────────────────────────────────────────
function InstanceResourcesView({ instanceId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    (async () => {
      try {
        const res = await api.getInstanceResources(instanceId);
        if (cancelled) return;
        if (res && res.ok === false) {
          setError(res.message || res.error || 'Failed to load resources');
          setData(null);
        } else {
          const payload = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
          setData(payload);
        }
      } catch (e) {
        if (!cancelled) { setError(e?.message || String(e)); setData(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [instanceId]);

  if (loading) {
    return <Card><Card.Body><div className="text-v2-ink-muted text-[12px]">Loading resources…</div></Card.Body></Card>;
  }
  if (error) {
    return (
      <Card><Card.Body>
        <div className="text-[12px] text-v2-danger border border-v2-danger rounded-v2-md px-3 py-2 bg-v2-danger-soft">{error}</div>
      </Card.Body></Card>
    );
  }
  if (!data) return null;

  const categories = [
    { key: 'agents',          label: 'Agents',           href: `/agentlets?instanceId=${instanceId}`, data: data.agents },
    { key: 'workflows',       label: 'Workflows',        href: `/workflows?instanceId=${instanceId}`, data: data.workflows },
    { key: 'connectors',      label: 'Connectors',       href: `/connectors?instanceId=${instanceId}`, data: data.connectors },
    { key: 'connectorGroups', label: 'Connector Groups', href: `/connectors?tab=groups&instanceId=${instanceId}`, data: data.connectorGroups },
  ];

  return (
    <div className="grid grid-cols-2 gap-3">
      {categories.map(cat => (
        <Card key={cat.key}>
          <Card.Header
            title={`${cat.label} · ${cat.data?.count ?? 0}`}
            actions={
              (cat.data?.count > 0) ? (
                <Button variant="ghost" size="sm" onClick={() => navigate(cat.href)}>
                  View all →
                </Button>
              ) : null
            }
          />
          <Card.Body>
            {!cat.data || cat.data.count === 0 ? (
              <div className="text-[12px] text-v2-ink-muted italic">
                No {cat.label.toLowerCase()} pinned to this instance.
              </div>
            ) : (
              <ul className="divide-y divide-v2-line">
                {(cat.data.sample || []).map(row => (
                  <li key={row.id} className="py-2 flex items-center gap-2 text-[12px]">
                    <span className="font-medium text-v2-ink truncate flex-1">{row.name || '(unnamed)'}</span>
                    {row.tag && <Pill tone="muted" size="sm">{row.tag}</Pill>}
                    <span className="text-[10.5px] text-v2-ink-muted">
                      {row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : ''}
                    </span>
                  </li>
                ))}
                {cat.data.count > (cat.data.sample?.length || 0) && (
                  <li className="py-2 text-[10.5px] text-v2-ink-muted italic">
                    + {cat.data.count - (cat.data.sample?.length || 0)} more
                  </li>
                )}
              </ul>
            )}
          </Card.Body>
        </Card>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// G541 — InstanceAuditView: timeline of lifecycle + promotion + grant
// events for the Instance. Backed by /api/v1/instances/{id}/audit.
// ──────────────────────────────────────────────────────────────────────
const AUDIT_EVENT_TONE = {
  INSTANCE_CREATED:   'success',
  INSTANCE_ARCHIVED:  'muted',
  PROMOTION_RECEIVED: 'cyan',
  PROMOTION_SENT:     'cyan',
  MEMBER_GRANTED:     'warning',
};

function InstanceAuditView({ instanceId }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    (async () => {
      try {
        const res = await api.getInstanceAudit(instanceId);
        if (cancelled) return;
        if (res && res.ok === false) {
          setError(res.message || res.error || 'Failed to load audit history');
          setRows([]);
        } else {
          const payload = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
          setRows(Array.isArray(payload) ? payload : []);
        }
      } catch (e) {
        if (!cancelled) { setError(e?.message || String(e)); setRows([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [instanceId]);

  if (loading) {
    return <Card><Card.Body><div className="text-v2-ink-muted text-[12px]">Loading audit history…</div></Card.Body></Card>;
  }
  if (error) {
    return (
      <Card><Card.Body>
        <div className="text-[12px] text-v2-danger border border-v2-danger rounded-v2-md px-3 py-2 bg-v2-danger-soft">{error}</div>
      </Card.Body></Card>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        icon={<Shield size={20} />}
        title="No audit events yet"
        description="Lifecycle events, promotion approvals and member grants on this instance will appear here as they happen."
      />
    );
  }

  return (
    <Card>
      <Card.Body>
        <table className="w-full text-[12px]">
          <thead className="text-left text-v2-ink-muted text-[10.5px] uppercase tracking-wider">
            <tr>
              <th className="py-2">When</th>
              <th>Event</th>
              <th>Actor</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-v2-line">
            {rows.map((r, idx) => (
              <tr key={r.sourceRowId ? `${r.source}-${r.sourceRowId}-${idx}` : idx}>
                <td className="py-2 text-v2-ink-muted whitespace-nowrap">
                  {r.ts ? new Date(r.ts).toLocaleString() : '—'}
                </td>
                <td>
                  <Pill tone={AUDIT_EVENT_TONE[r.eventType] || 'muted'} size="sm">{r.eventType}</Pill>
                </td>
                <td className="text-v2-ink-muted">{r.actorName || (r.actorUserId ? r.actorUserId.slice(0,8) + '…' : '—')}</td>
                <td className="text-v2-ink">{r.summary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card.Body>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// OrgDetailPanel
// ──────────────────────────────────────────────────────────────────────
// Identifier value with an explicit copy button + brief confirmation.
function CopyId({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-2">
      <span className="font-mono-v2 text-v2-ink">{value}</span>
      <button
        type="button"
        className="inline-flex items-center text-v2-ink-muted hover:text-v2-action"
        title={copied ? 'Copied' : 'Copy'}
        aria-label="Copy identifier"
        onClick={() => {
          try { navigator.clipboard?.writeText(String(value)); } catch (e) { /* clipboard unavailable */ }
          setCopied(true); setTimeout(() => setCopied(false), 1200);
        }}
      >
        {copied ? <Check size={13} className="text-v2-success" /> : <Copy size={13} />}
      </button>
    </span>
  );
}

function OrgDetailPanel({ org, tenants: tenantsProp, onEdit, onDelete, canEdit, canDelete }) {
  const tenants = Array.isArray(tenantsProp) ? tenantsProp : [];
  const orgTenants = tenants.filter(t => t.orgId === org.id);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Building2 size={18} className="text-v2-ink-muted" />
        <h2 className="text-[16px] font-semibold text-v2-ink flex-1">{org.name}</h2>
        {canEdit && (
          <Button variant="ghost" size="sm" iconLeft={<Edit2 size={12} />} onClick={() => onEdit && onEdit(org)}>Edit</Button>
        )}
        {canDelete && (
          <Button variant="ghost" size="sm" iconLeft={<Trash2 size={12} />} onClick={() => onDelete && onDelete(org)}
                  className="!text-v2-danger hover:!bg-v2-danger-soft">Delete</Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <MetricCard label="Tenants" value={orgTenants.length} />
        <MetricCard label="Domain" value={org.domain || '—'} />
        <MetricCard label="Created" value={org.createdAt ? new Date(org.createdAt).toLocaleDateString() : '—'} />
      </div>

      <Card>
        <Card.Header title="Identifiers" />
        <Card.Body>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-[12px]">
            <dt className="text-v2-ink-muted">Org ID</dt>
            <dd><CopyId value={org.id} /></dd>
            {(org.customerId || org.customer_id) && (
              <>
                <dt className="text-v2-ink-muted">Customer ID</dt>
                <dd><CopyId value={org.customerId || org.customer_id} /></dd>
              </>
            )}
          </dl>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header title="Tenants in this Org" />
        <Card.Body>
          {orgTenants.length === 0 ? (
            <EmptyState icon={<Layers size={20} />} title="No tenants yet" />
          ) : (
            <ul className="divide-y divide-v2-line">
              {orgTenants.map(t => (
                <li key={t.id} className="py-2 flex items-center gap-2">
                  <Layers size={14} className="text-v2-ink-muted" />
                  <div className="flex-1">
                    {/* RC-25 / R1 — Tenant row uses EntityLabel for the
                        "{name} ({shortid})" composition. Previously rendered
                        the full 36-char UUID on a second line — operationally
                        noisy and inconsistent with the platform standard. */}
                    <EntityLabel kind="tenant" id={t.id} name={t.name} size="md" />
                  </div>
                  <Pill tone={STATUS_TONE[(t.status || 'ACTIVE').toUpperCase()] || 'muted'} size="sm">
                    {(t.status || 'active').toLowerCase()}
                  </Pill>
                </li>
              ))}
            </ul>
          )}
        </Card.Body>
      </Card>

      {/* G532 — Org-level members: every UserOrgRole grant on this org,
          regardless of tenant or instance scoping. */}
      <Card>
        <Card.Header title="Members of this Organization" />
        <Card.Body>
          <MembersList kind="org" scopeId={org.id} />
        </Card.Body>
      </Card>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// G552 / G575 — TenantPromotionGraphView
//
// Layered DAG visualization of declared promotion paths.
//
// **Layout algorithm (Sugiyama-style):**
//   1. Compute node RANK = longest path from any source instance (a node
//      with no incoming declared edges). This collapses linear chains and
//      naturally fans out merge points.
//   2. For each rank, order nodes by barycentric average of parent y-index
//      to minimize edge crossings on fan-in/fan-out topologies (e.g.
//      Staging → {UAT, Stage2, PreProd} → Prod).
//   3. Vertically re-center each rank so the diagram reads balanced even
//      when one rank has 5 nodes and the next has 1.
//
// **Production-path highlighting (G575):**
//   We backward-walk every PRODUCTION-tier or isProduction=true sink to
//   compute the set of nodes + edges that ultimately feed into prod. Those
//   are drawn SOLID, full-color. Off-path edges (e.g. a Staging → UAT lane
//   that doesn't continue to PreProd → Prod) draw DASHED + muted. Disabled
//   edges draw DOTTED. This makes complex enterprise topologies legible at
//   a glance — operators see which lanes ultimately matter for production
//   releases vs which are experimental side-tracks.
//
// Tenant Admins (canEdit=true) get + Add Path, toggle-enable, delete-edge
// affordances. Read-only roles see the same graph without controls.
// ──────────────────────────────────────────────────────────────────────

const TIER_NODE_TONE = {
  PRODUCTION: { bg: '#fef2f2', border: '#dc2626', ink: '#991b1b' },
  STAGING:    { bg: '#fffbeb', border: '#d97706', ink: '#92400e' },
  DEV:        { bg: '#ecfeff', border: '#0891b2', ink: '#155e75' },
  SYSTEM:     { bg: '#f5f5f4', border: '#78716c', ink: '#44403c' },
  CUSTOM:     { bg: '#f5f5f4', border: '#78716c', ink: '#44403c' },
};

/* On-path emphasis color — the teal that signals "this lane drives
 * production releases". Mirrors v2-action token for consistency with
 * primary buttons. */
const ON_PATH_STROKE   = '#0d9488';
const ON_PATH_NODE_BG  = '#f0fdfa';
const OFF_PATH_STROKE  = '#94a3b8';
const DISABLED_STROKE  = '#cbd5e1';

function TenantPromotionGraphView({ tenant, instances, canEdit }) {
  const [paths, setPaths] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // { path, mode: 'edit' }
  const [deleting, setDeleting] = useState(null); // path being confirm-deleted
  const [busyId, setBusyId] = useState(null);     // optimistic mutation guard
  const toast = useToast();
  /* G577 — "You are here" emphasis. The user's currently-active Instance
   * (the one their JWT iid claim points at) gets the only colored fill +
   * teal ring + "Current" badge in the entire graph; every other node
   * collapses to a neutral grey-outlined card with the tier color
   * confined to the small label below the name. The operator answers
   * "where in the release lifecycle am I?" by finding the one colored
   * node — not by trying to remember which tier color means which env. */
  const { session } = useAuth();
  const activeInstanceId = session?.instanceId || null;

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.getPromotionPaths(tenant.id);
      if (res && res.ok === false) {
        setError(res.message || res.error || 'Failed to load promotion graph');
        setPaths([]);
      } else {
        const payload = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
        setPaths(Array.isArray(payload) ? payload : []);
      }
    } catch (e) {
      setError(e?.message || String(e));
      setPaths([]);
    } finally {
      setLoading(false);
    }
  }, [tenant.id]);

  useEffect(() => { reload(); }, [reload]);

  /* Layout constants for the SVG. Symbolic so style tweaks don't require
   * re-deriving arrow geometry. NODE_W intentionally wider than the old
   * tier-column layout — tier+production badges need room without
   * truncating the instance name on real-world tenant topologies. */
  const NODE_W = 196;
  const NODE_H = 64;
  const COL_GAP = 96;
  const ROW_GAP = 22;
  const PAD_X = 24;
  const PAD_Y = 36;

  /* G575 — Layered DAG layout + production-path reachability.
   *
   * Replaces the original tier-column layout (which conflated PreProd /
   * UAT / Stage2 all into the CUSTOM column and produced overlapping
   * bezier arcs on fan-out). The new layout uses topological rank +
   * within-rank barycentric ordering so a real enterprise topology like
   *   Dev → Staging → {Stage2, UAT, PreProd} → ... → Prod
   * reads naturally without overlap.
   *
   * We also compute the set of nodes + edges that feed into a PRODUCTION
   * sink (backward walk from every PRODUCTION-tier or isProduction=true
   * node). The render layer dims off-path lanes — visually answering the
   * operator's first question: "which routes actually reach Production?". */
  const layout = useMemo(() => {
    const allInstances = instances || [];
    const pathList = paths || [];
    if (allInstances.length === 0) {
      return { positions: {}, width: 0, height: 0, onPathNodes: new Set(), onPathEdges: new Set() };
    }

    /* Adjacency lists — forward for cycle-free rank, reverse for the
     * production-reachability backward walk + barycentric scoring. */
    const adj = new Map();   // id → [{ to, edge }]
    const rev = new Map();   // id → [{ from, edge }]
    for (const inst of allInstances) {
      adj.set(inst.id, []);
      rev.set(inst.id, []);
    }
    for (const p of pathList) {
      if (adj.has(p.fromInstanceId) && adj.has(p.toInstanceId)) {
        adj.get(p.fromInstanceId).push({ to: p.toInstanceId, edge: p });
        rev.get(p.toInstanceId).push({ from: p.fromInstanceId, edge: p });
      }
    }

    /* Step 1 — rank = longest path from any source (no incoming edges).
     * Kahn's algorithm in topological order; cycles are rejected at
     * declare-time server-side, so the queue covers every reachable node. */
    const rank = new Map();
    const remaining = new Map();
    for (const inst of allInstances) {
      rank.set(inst.id, 0);
      remaining.set(inst.id, rev.get(inst.id).length);
    }
    const queue = [];
    for (const inst of allInstances) {
      if (remaining.get(inst.id) === 0) queue.push(inst.id);
    }
    let head = 0;
    while (head < queue.length) {
      const u = queue[head++];
      for (const { to } of adj.get(u)) {
        rank.set(to, Math.max(rank.get(to), rank.get(u) + 1));
        remaining.set(to, remaining.get(to) - 1);
        if (remaining.get(to) === 0) queue.push(to);
      }
    }
    /* Isolated nodes (no edges either way) stay at rank 0, which puts
     * them in the leftmost lane with the actual sources — acceptable
     * because they're outliers the operator probably wants to see anyway. */

    /* Step 2 — production reachability (backward walk from prod sinks). */
    const isProd = (inst) => inst.tier === 'PRODUCTION' || inst.isProduction === true;
    const onPathNodes = new Set();
    const onPathEdges = new Set();
    const stack = [];
    for (const inst of allInstances) {
      if (isProd(inst)) {
        onPathNodes.add(inst.id);
        stack.push(inst.id);
      }
    }
    while (stack.length) {
      const u = stack.pop();
      for (const { from, edge } of rev.get(u) || []) {
        /* Only follow ENABLED edges. The whole point of the on-path
         * encoding is "currently reaches Production"; a disabled edge
         * breaks that reachability the moment a Tenant Admin toggles it.
         * Matches user intent: "if an env moved OUT OF the production
         * path it should be highlighted (dotted lines or something)" —
         * disabling its only outbound to prod is exactly that move. */
        if (!edge.enabled) continue;
        onPathEdges.add(edge.id);
        if (!onPathNodes.has(from)) {
          onPathNodes.add(from);
          stack.push(from);
        }
      }
    }

    /* Step 3 — bucket nodes by rank, then within-rank barycentric ordering. */
    const ranks = [];
    const buckets = new Map();
    for (const inst of allInstances) {
      const r = rank.get(inst.id);
      if (!buckets.has(r)) {
        buckets.set(r, []);
        ranks.push(r);
      }
      buckets.get(r).push(inst);
    }
    ranks.sort((a, b) => a - b);

    /* Stable seed: alphabetical within each rank. */
    for (const r of ranks) buckets.get(r).sort((a, b) => a.name.localeCompare(b.name));

    /* Two-pass barycentric: each child's preferred index is the average of
     * its parents' indices. Two passes is enough to settle the typical 3-5
     * rank topologies we expect in customer tenants. */
    const yIndex = new Map();
    for (const r of ranks) {
      const bucket = buckets.get(r);
      if (r === ranks[0]) {
        bucket.forEach((inst, idx) => yIndex.set(inst.id, idx));
        continue;
      }
      const scored = bucket.map(inst => {
        const parents = (rev.get(inst.id) || [])
          .map(({ from }) => yIndex.get(from))
          .filter(v => v !== undefined);
        const score = parents.length === 0
          ? Number.MAX_SAFE_INTEGER
          : parents.reduce((a, b) => a + b, 0) / parents.length;
        return { inst, score };
      });
      scored.sort((a, b) => a.score - b.score);
      scored.forEach(({ inst }, idx) => yIndex.set(inst.id, idx));
      buckets.set(r, scored.map(s => s.inst));
    }

    /* Step 4 — assign x/y. Center each rank vertically against the tallest
     * rank so the canvas doesn't look top-heavy. */
    const maxBucket = Math.max(...ranks.map(r => buckets.get(r).length));
    const positions = {};
    for (const r of ranks) {
      const bucket = buckets.get(r);
      const totalH = bucket.length * NODE_H + (bucket.length - 1) * ROW_GAP;
      const tallestH = maxBucket * NODE_H + (maxBucket - 1) * ROW_GAP;
      const yOffset = PAD_Y + (tallestH - totalH) / 2;
      const rankIdx = ranks.indexOf(r);
      bucket.forEach((inst, idx) => {
        positions[inst.id] = {
          x: PAD_X + rankIdx * (NODE_W + COL_GAP),
          y: yOffset + idx * (NODE_H + ROW_GAP),
          instance: inst,
          onPath: onPathNodes.has(inst.id),
          /* G577 — expose rankIndex so the SVG renderer can detect
           * long-rank edges (Dev → Prod that skip Staging) and route
           * them via a flyover arc instead of cutting through the
           * intermediate rank's vertical band. */
          rankIndex: rankIdx,
        };
      });
    }

    const width = PAD_X * 2 + ranks.length * NODE_W + Math.max(0, ranks.length - 1) * COL_GAP;
    const height = PAD_Y * 2 + maxBucket * NODE_H + Math.max(0, maxBucket - 1) * ROW_GAP;
    return { positions, width, height, onPathNodes, onPathEdges };
  }, [instances, paths]);

  /* useToast() returns { toasts, addToast, removeToast }. There is no
   * .success() / .error() helper — callers pass tone via the second
   * positional argument (v1 signature) or via an object (v2). We use
   * the v2 object form so the v2 Toast renderer picks up the tone. */
  const handleTogglePath = useCallback(async (path) => {
    setBusyId(path.id);
    try {
      const res = await api.updatePromotionPath(tenant.id, path.id, { enabled: !path.enabled });
      if (res && res.ok === false) {
        toast.addToast({ tone: 'danger', message: res.message || res.error || 'Failed to toggle path' });
        return;
      }
      toast.addToast({ tone: 'success', message: path.enabled ? 'Path disabled' : 'Path enabled' });
      await reload();
    } catch (e) {
      toast.addToast({ tone: 'danger', message: e?.message || 'Failed to toggle path' });
    } finally {
      setBusyId(null);
    }
  }, [tenant.id, reload, toast]);

  const handleDeletePath = useCallback(async () => {
    if (!deleting) return;
    setBusyId(deleting.id);
    try {
      const res = await api.deletePromotionPath(tenant.id, deleting.id);
      if (res && res.ok === false) {
        toast.addToast({ tone: 'danger', message: res.message || res.error || 'Failed to delete path' });
        return;
      }
      toast.addToast({ tone: 'success', message: 'Promotion path removed' });
      setDeleting(null);
      await reload();
    } catch (e) {
      toast.addToast({ tone: 'danger', message: e?.message || 'Failed to delete path' });
    } finally {
      setBusyId(null);
    }
  }, [deleting, tenant.id, reload, toast]);

  if (loading && paths === null) {
    return <Card><Card.Body><div className="text-[12px] text-v2-ink-muted">Loading promotion graph…</div></Card.Body></Card>;
  }
  if (error) {
    return (
      <Card><Card.Body>
        <div className="text-[12px] text-v2-danger border border-v2-danger rounded-v2-md px-3 py-2 bg-v2-danger-soft">{error}</div>
      </Card.Body></Card>
    );
  }

  const hasInstances = (instances || []).length > 0;
  const hasPaths = (paths || []).length > 0;

  return (
    <div className="space-y-3">
      {/* Header row: title + Add button. Keep title plain — the tab label
       *  already says "Promotion Graph", so we just need to anchor the
       *  add affordance and a short explainer. */}
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="text-[13px] font-medium text-v2-ink">Declared promotion paths</div>
          <div className="text-[11.5px] text-v2-ink-muted mt-0.5">
            Allowed directional moves between this tenant's instances. Promotions outside the declared graph are rejected.
          </div>
        </div>
        {canEdit && hasInstances && (instances.length >= 2) && (
          <Button variant="primary" size="sm" iconLeft={<Plus size={12} />}
                  onClick={() => setAddOpen(true)}>Add path</Button>
        )}
      </div>

      {!hasInstances && (
        <EmptyState
          icon={<Activity size={20} />}
          title="No instances yet"
          description="Create at least two instances under this tenant before declaring promotion paths between them."
        />
      )}

      {hasInstances && !hasPaths && (
        <EmptyState
          icon={<Activity size={20} />}
          title="No promotion paths declared"
          description={canEdit
            ? 'Click + Add path to declare an allowed move (for example, Dev → Staging → Production). Promotion attempts that try to move resources outside these paths are rejected.'
            : 'A tenant administrator can declare allowed moves between this tenant\'s instances here. Promotion attempts outside the declared paths are rejected.'}
        />
      )}

      {hasInstances && hasPaths && (
        <>
          <Card>
            <Card.Body>
              {/* G575 + G577 — Legend strip. Reorganized so the FIRST
               *  cue the operator sees is the "you are here" marker, then
               *  the line-style encoding for on/off/disabled. */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pb-3 mb-3 border-b border-v2-line text-[11px] text-v2-ink-muted">
                {activeInstanceId && layout.positions[activeInstanceId] && (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="relative inline-flex">
                      <span className="inline-block w-3 h-3 rounded-sm" style={{ background: '#f0fdfa', border: `1.5px solid ${ON_PATH_STROKE}` }} />
                      <span className="absolute -top-0.5 -right-0.5 inline-block w-1.5 h-1.5 rounded-full" style={{ background: '#10b981' }} />
                    </span>
                    <span>Your active scope</span>
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5">
                  <svg width="32" height="8" aria-hidden="true">
                    <line x1="0" y1="4" x2="28" y2="4" stroke={ON_PATH_STROKE} strokeWidth="1.8" />
                    <polygon points="28,2 32,4 28,6" fill={ON_PATH_STROKE} />
                  </svg>
                  <span>On production path</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <svg width="32" height="8" aria-hidden="true">
                    <line x1="0" y1="4" x2="28" y2="4" stroke={OFF_PATH_STROKE} strokeWidth="1.4" strokeDasharray="2 5" />
                    <polygon points="28,2 32,4 28,6" fill={OFF_PATH_STROKE} />
                  </svg>
                  <span>Off-path (won't reach Production)</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <svg width="32" height="8" aria-hidden="true">
                    <line x1="0" y1="4" x2="28" y2="4" stroke={DISABLED_STROKE} strokeWidth="1.4" strokeDasharray="8 4" />
                    <polygon points="28,2 32,4 28,6" fill={DISABLED_STROKE} />
                  </svg>
                  <span>Disabled (declared, not active)</span>
                </span>
                <span className="ml-auto text-v2-ink-muted">
                  {layout.onPathNodes.size} of {(instances || []).length} instances reach Production
                </span>
              </div>

              <div className="overflow-x-auto">
                <svg
                  width={Math.max(layout.width, 320)}
                  /* G577 — extra top padding for the flyover arc region
                   *  where long-rank edges (Dev → Prod) arc over the
                   *  intermediate rank. */
                  height={Math.max(layout.height + 24, 80)}
                  role="img"
                  aria-label={`Promotion graph for ${tenant.name}`}
                  className="block"
                >
                  <defs>
                    {/* Three arrow markers, sized down for thinner lines. */}
                    <marker id={`pgArrowOn-${tenant.id}`} viewBox="0 0 10 10" refX="9" refY="5"
                            markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                      <path d="M 0 0 L 10 5 L 0 10 z" fill={ON_PATH_STROKE} />
                    </marker>
                    <marker id={`pgArrowOff-${tenant.id}`} viewBox="0 0 10 10" refX="9" refY="5"
                            markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                      <path d="M 0 0 L 10 5 L 0 10 z" fill={OFF_PATH_STROKE} />
                    </marker>
                    <marker id={`pgArrowDisabled-${tenant.id}`} viewBox="0 0 10 10" refX="9" refY="5"
                            markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                      <path d="M 0 0 L 10 5 L 0 10 z" fill={DISABLED_STROKE} />
                    </marker>
                  </defs>

                  {/* Edges. G577 routing: short edges (rank-span 1) use
                   *  a smooth S-curve as before; LONG edges (rank-span
                   *  >= 2) arc OVER the intermediate ranks via a flyover
                   *  path — control Y is lifted above the topmost node
                   *  row so the curve clearly wraps around / over
                   *  intermediate boxes instead of cutting through them. */}
                  {(paths || []).map(p => {
                    const a = layout.positions[p.fromInstanceId];
                    const b = layout.positions[p.toInstanceId];
                    if (!a || !b) return null;

                    const archived = a.instance.status === 'ARCHIVED' || b.instance.status === 'ARCHIVED';
                    const onPath = layout.onPathEdges.has(p.id);
                    /* G577 — thinner strokes across the board for a more
                     *  refined enterprise feel. Visual hierarchy preserved
                     *  via stroke-width delta (on-path bumped slightly). */
                    let stroke, dash, markerId, strokeWidth, opacity;
                    if (!p.enabled) {
                      stroke = DISABLED_STROKE;
                      dash = '8 4';
                      markerId = `pgArrowDisabled-${tenant.id}`;
                      strokeWidth = 1.2;
                      opacity = 0.85;
                    } else if (onPath) {
                      stroke = ON_PATH_STROKE;
                      dash = '0';
                      markerId = `pgArrowOn-${tenant.id}`;
                      strokeWidth = 1.6;
                      opacity = archived ? 0.55 : 1;
                    } else {
                      stroke = OFF_PATH_STROKE;
                      dash = '2 5';
                      markerId = `pgArrowOff-${tenant.id}`;
                      strokeWidth = 1.4;
                      opacity = archived ? 0.5 : 0.85;
                    }

                    const x1 = a.x + NODE_W;
                    const y1 = a.y + NODE_H / 2;
                    const x2 = b.x;
                    const y2 = b.y + NODE_H / 2;
                    const rankSpan = (b.rankIndex || 0) - (a.rankIndex || 0);

                    /* G577 — flyover routing for long-rank edges. The arc
                     *  needs to clear the topmost node row of every
                     *  intermediate rank. We lift the bezier control Y
                     *  above PAD_Y by (rankSpan - 1) * 18 — empirically
                     *  enough breathing room without ballooning the
                     *  canvas, and capped at -24 so the arc never goes
                     *  off the top of the SVG. */
                    let path;
                    if (rankSpan >= 2) {
                      const flyoverY = Math.max(8, PAD_Y - 8 - (rankSpan - 1) * 18);
                      const exitX = x1 + 24;
                      const enterX = x2 - 24;
                      path = `M ${x1} ${y1} `
                           + `C ${exitX} ${y1}, ${exitX} ${flyoverY}, ${(x1 + x2) / 2} ${flyoverY} `
                           + `S ${enterX} ${y2}, ${x2} ${y2}`;
                    } else {
                      /* Short edge: simple S-curve. */
                      const dx = Math.max(40, (x2 - x1) * 0.55);
                      path = `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
                    }

                    const tooltip = `${p.fromName} → ${p.toName}`
                      + (p.enabled ? '' : ' (disabled)')
                      + (onPath ? '' : ' · off production path')
                      + (p.notes ? `\n${p.notes}` : '');

                    return (
                      <g key={p.id}>
                        <path d={path}
                              fill="none"
                              stroke={stroke}
                              strokeWidth={strokeWidth}
                              strokeDasharray={dash}
                              opacity={opacity}
                              markerEnd={`url(#${markerId})`} />
                        <title>{tooltip}</title>
                      </g>
                    );
                  })}

                  {/* G577 — Nodes redesigned around "you are here" emphasis.
                   *  - Active scope: light teal fill + teal ring + green
                   *    dot indicator in the corner + "● Current" label.
                   *  - Everything else: neutral white card with thin
                   *    grey border (or grey dashed border if off-path).
                   *    Tier color is now restricted to the SMALL TIER
                   *    LABEL below the instance name — no longer the
                   *    dominant cue. This means the operator's eye lands
                   *    on the one colored node immediately.
                   *  - Off-path: dashed border + reduced opacity + label
                   *    suffix "· off-path" preserved.
                   *  - Archived: 50% opacity overlay. */}
                  {Object.values(layout.positions).map(({ x, y, instance: inst, onPath }) => {
                    const tone = TIER_NODE_TONE[inst.tier] || TIER_NODE_TONE.CUSTOM;
                    const archived = inst.status === 'ARCHIVED';
                    const isProductionNode = inst.tier === 'PRODUCTION' || inst.isProduction === true;
                    const isActive = inst.id === activeInstanceId;
                    const baseOpacity = archived ? 0.5 : (isActive ? 1 : onPath ? 0.95 : 0.65);

                    /* Border + fill: ACTIVE wins; otherwise neutral grey. */
                    const fill = isActive ? '#f0fdfa' : '#ffffff';
                    const stroke = isActive ? ON_PATH_STROKE : '#d4d4d8';
                    const strokeWidth = isActive ? 2 : 1;
                    const strokeDash = onPath || isActive ? '0' : '4 3';

                    return (
                      <g key={inst.id} transform={`translate(${x} ${y})`} opacity={baseOpacity}>
                        {/* Subtle drop shadow on the active node only,
                         *  for a slight pop-out effect. */}
                        {isActive && !archived && (
                          <rect width={NODE_W} height={NODE_H} rx="9" x="0.5" y="2"
                                fill="rgba(13, 148, 136, 0.18)" />
                        )}
                        <rect width={NODE_W} height={NODE_H} rx="8"
                              fill={fill}
                              stroke={stroke}
                              strokeWidth={strokeWidth}
                              strokeDasharray={strokeDash} />
                        {/* Instance name — neutral dark text, bold. */}
                        <text x="14" y="24" fontSize="13" fontWeight="600" fill="#18181b">
                          {inst.name.length > 24 ? inst.name.slice(0, 23) + '…' : inst.name}
                        </text>
                        {/* Tier label — the ONLY place tier color lives. */}
                        <text x="14" y="44" fontSize="10.5" fontWeight="600"
                              fill={tone.border}
                              letterSpacing="0.4">
                          {inst.tier}
                          {isProductionNode && (
                            <tspan fill={TIER_NODE_TONE.PRODUCTION.border} fontWeight="700"> · PROD</tspan>
                          )}
                          {archived && <tspan fill="#71717a"> · archived</tspan>}
                          {!onPath && !archived && !isActive && (
                            <tspan fill="#a8a29e" fontStyle="italic" fontWeight="500"> · off-path</tspan>
                          )}
                        </text>
                        {/* "Current" indicator: green dot in top-right corner + small badge */}
                        {isActive && (
                          <>
                            <circle cx={NODE_W - 12} cy={12} r={4} fill="#10b981" />
                            <circle cx={NODE_W - 12} cy={12} r={4} fill="none"
                                    stroke="#10b981" strokeWidth="1.5" opacity="0.4">
                              {/* Subtle pulse — re-purposed by browsers that
                               *  respect prefers-reduced-motion via CSS;
                               *  static halo otherwise. */}
                            </circle>
                            <text x={NODE_W - 22} y={16} fontSize="9" fontWeight="700"
                                  fill={ON_PATH_STROKE} textAnchor="end" letterSpacing="0.5">
                              CURRENT
                            </text>
                          </>
                        )}
                      </g>
                    );
                  })}
                </svg>
              </div>
            </Card.Body>
          </Card>

          {/* Edge inventory table — accessible, gives the per-edge controls. */}
          <Card>
            <Card.Body>
              <table className="w-full text-[12px]">
                <thead className="text-left text-v2-ink-muted text-[10.5px] uppercase tracking-wider">
                  <tr>
                    <th className="py-2">From</th>
                    <th>To</th>
                    <th>State</th>
                    <th>Notes</th>
                    {canEdit && <th aria-label="Actions" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-v2-line">
                  {(paths || []).map(p => (
                    <tr key={p.id}>
                      <td className="py-2">
                        <div className="font-medium text-v2-ink">{p.fromName}</div>
                        <Pill tone={TIER_TONE[p.fromTier] || 'muted'} size="sm">{p.fromTier || '—'}</Pill>
                      </td>
                      <td>
                        <div className="font-medium text-v2-ink">{p.toName}</div>
                        <Pill tone={TIER_TONE[p.toTier] || 'muted'} size="sm">{p.toTier || '—'}</Pill>
                      </td>
                      <td>
                        {p.enabled
                          ? <Pill tone="success" size="sm" dot>Enabled</Pill>
                          : <Pill tone="muted" size="sm">Disabled</Pill>}
                      </td>
                      <td className="text-v2-ink-muted">{p.notes || '—'}</td>
                      {canEdit && (
                        <td className="text-right whitespace-nowrap">
                          <Button variant="ghost" size="sm"
                                  disabled={busyId === p.id}
                                  onClick={() => handleTogglePath(p)}>
                            {p.enabled ? 'Disable' : 'Enable'}
                          </Button>
                          <Button variant="ghost" size="sm" iconLeft={<Edit2 size={12} />}
                                  onClick={() => setEditing(p)}>Edit</Button>
                          <Button variant="ghost" size="sm" iconLeft={<Trash2 size={12} />}
                                  className="!text-v2-danger hover:!bg-v2-danger-soft"
                                  onClick={() => setDeleting(p)}>Delete</Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card.Body>
          </Card>
        </>
      )}

      {/* Add slideout */}
      <AddPromotionPathSlideout
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        tenantId={tenant.id}
        instances={instances || []}
        existingPaths={paths || []}
        onCreated={async () => { setAddOpen(false); await reload(); }}
      />

      {/* Edit slideout (notes-only; endpoints are immutable per backend contract) */}
      <EditPromotionPathSlideout
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        tenantId={tenant.id}
        path={editing}
        onSaved={async () => { setEditing(null); await reload(); }}
      />

      {/* Delete confirm */}
      <Modal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        title="Remove promotion path"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button variant="danger" onClick={handleDeletePath}
                    disabled={busyId === deleting?.id}>Remove path</Button>
          </>
        }
      >
        {deleting && (
          <div className="space-y-3 text-[13px]">
            <p>
              Remove the path <strong>{deleting.fromName}</strong> → <strong>{deleting.toName}</strong>?
            </p>
            <p className="text-v2-ink-muted text-[12px]">
              Future promotion attempts on this move will be rejected. The instances themselves are untouched.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── G552 — Add / Edit slideouts for promotion paths ─────────────────────

function AddPromotionPathSlideout({ isOpen, onClose, tenantId, instances, existingPaths, onCreated }) {
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      setFromId(''); setToId(''); setNotes(''); setFormError(null); setSubmitting(false);
    }
  }, [isOpen]);

  // Block obviously bad picks client-side so the user gets immediate
  // feedback. Server re-validates and is the source of truth.
  const wouldDuplicate = useMemo(
    () => fromId && toId && existingPaths.some(p => p.fromInstanceId === fromId && p.toInstanceId === toId),
    [fromId, toId, existingPaths]
  );
  const isSelfLoop = fromId && toId && fromId === toId;

  const submit = async () => {
    if (!fromId || !toId) { setFormError('Choose both a source and a target instance.'); return; }
    if (isSelfLoop) { setFormError('A path cannot point to itself.'); return; }
    if (wouldDuplicate) { setFormError('This path already exists.'); return; }
    setSubmitting(true); setFormError(null);
    try {
      const res = await api.createPromotionPath(tenantId, {
        fromInstanceId: fromId, toInstanceId: toId, enabled: true, notes: notes || null,
      });
      if (res && res.ok === false) {
        setFormError(res.message || res.error || 'Failed to add path');
        return;
      }
      toast.addToast({ tone: 'success', message: 'Promotion path added' });
      onCreated && onCreated();
    } catch (e) {
      setFormError(e?.message || 'Failed to add path');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      header={<div className="font-semibold text-v2-ink">Add promotion path</div>}
      ariaLabel="Add promotion path"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={submit}
                  disabled={submitting || !fromId || !toId || isSelfLoop || wouldDuplicate}>
            {submitting ? 'Adding…' : 'Add path'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-[12px] text-v2-ink-muted">
          Declare an allowed promotion move from a source instance to a target. The new edge cannot be a self-loop, a duplicate, or close a cycle in the existing graph.
        </p>

        <label className="block">
          <div className="text-[11.5px] uppercase tracking-wider text-v2-ink-muted mb-1">Source instance</div>
          <select
            className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2 py-1.5 text-[13px] bg-v2-surface"
            value={fromId}
            onChange={(e) => setFromId(e.target.value)}
          >
            <option value="">— select source —</option>
            {instances.map(i => (
              <option key={i.id} value={i.id}>{i.name} · {i.tier}{i.status === 'ARCHIVED' ? ' (archived)' : ''}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <div className="text-[11.5px] uppercase tracking-wider text-v2-ink-muted mb-1">Target instance</div>
          <select
            className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2 py-1.5 text-[13px] bg-v2-surface"
            value={toId}
            onChange={(e) => setToId(e.target.value)}
          >
            <option value="">— select target —</option>
            {instances.filter(i => i.id !== fromId).map(i => (
              <option key={i.id} value={i.id}>{i.name} · {i.tier}{i.status === 'ARCHIVED' ? ' (archived)' : ''}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <div className="text-[11.5px] uppercase tracking-wider text-v2-ink-muted mb-1">Notes (optional)</div>
          <textarea
            className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2 py-1.5 text-[13px] bg-v2-surface"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Why does this path exist? E.g. 'Approved DR fallback'."
          />
        </label>

        {(isSelfLoop || wouldDuplicate || formError) && (
          <div className="text-[12px] text-v2-danger border border-v2-danger rounded-v2-md px-3 py-2 bg-v2-danger-soft">
            {isSelfLoop ? 'Source and target must be different.'
              : wouldDuplicate ? 'This path is already declared.'
              : formError}
          </div>
        )}
      </div>
    </Slideout>
  );
}

function EditPromotionPathSlideout({ isOpen, onClose, tenantId, path, onSaved }) {
  const [enabled, setEnabled] = useState(true);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const toast = useToast();

  useEffect(() => {
    if (isOpen && path) {
      setEnabled(!!path.enabled);
      setNotes(path.notes || '');
      setFormError(null);
      setSubmitting(false);
    }
  }, [isOpen, path]);

  const submit = async () => {
    if (!path) return;
    setSubmitting(true); setFormError(null);
    try {
      const res = await api.updatePromotionPath(tenantId, path.id, {
        enabled,
        notes,
      });
      if (res && res.ok === false) {
        setFormError(res.message || res.error || 'Failed to save changes');
        return;
      }
      toast.addToast({ tone: 'success', message: 'Promotion path updated' });
      onSaved && onSaved();
    } catch (e) {
      setFormError(e?.message || 'Failed to save changes');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      header={
        <div className="font-semibold text-v2-ink">
          Edit path · {path?.fromName} → {path?.toName}
        </div>
      }
      ariaLabel="Edit promotion path"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      }
    >
      {path && (
        <div className="space-y-3">
          <p className="text-[12px] text-v2-ink-muted">
            Endpoints are immutable — to change them, delete this path and add a new one. The cycle-detection check stays consistent that way.
          </p>

          <label className="flex items-center gap-2 text-[13px] text-v2-ink">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="v2-focus-ring"
            />
            <span>Enabled (allow promotions on this path)</span>
          </label>

          <label className="block">
            <div className="text-[11.5px] uppercase tracking-wider text-v2-ink-muted mb-1">Notes</div>
            <textarea
              className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2 py-1.5 text-[13px] bg-v2-surface"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>

          {formError && (
            <div className="text-[12px] text-v2-danger border border-v2-danger rounded-v2-md px-3 py-2 bg-v2-danger-soft">
              {formError}
            </div>
          )}
        </div>
      )}
    </Slideout>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Phase 9 — TenantPromotionsView
//
// Newest-first history of every promotion event under a tenant. The
// Promotion Graph above declares ALLOWED moves; this view shows what's
// HAPPENED — initiated, awaiting approval, promoted, rejected, failed,
// or rolled back. Status filter chips at the top; rollback affordance
// on PROMOTED rows (with a required reason); cancel on PENDING rows
// when the caller is the initiator.
// ──────────────────────────────────────────────────────────────────────
const PROMOTION_STATUS_TONE = {
  PENDING_APPROVAL: 'warning',
  APPROVED:         'cyan',
  PROMOTED:         'success',
  REJECTED:         'muted',
  FAILED:           'danger',
  ROLLED_BACK:      'muted',
};

const PROMOTION_STATUS_LABEL = {
  PENDING_APPROVAL: 'Pending approval',
  APPROVED:         'Approved',
  PROMOTED:         'Promoted',
  REJECTED:         'Rejected',
  FAILED:           'Failed',
  ROLLED_BACK:      'Rolled back',
};

function TenantPromotionsView({ tenantId, canRollback }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(''); // '' = all
  const [busyId, setBusyId] = useState(null);
  const [rollbackTarget, setRollbackTarget] = useState(null); // event being rolled back
  const [rollbackReason, setRollbackReason] = useState('');
  const toast = useToast();

  const reload = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true); setError(null);
    try {
      const res = await api.listPromotions(tenantId, statusFilter || undefined);
      if (res && res.ok === false) {
        setError(res.message || res.error || 'Failed to load promotion history');
        setRows([]);
      } else {
        const payload = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
        setRows(Array.isArray(payload) ? payload : []);
      }
    } catch (e) {
      setError(e?.message || String(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, statusFilter]);

  useEffect(() => { reload(); }, [reload]);

  const handleRollback = useCallback(async () => {
    if (!rollbackTarget) return;
    if (!rollbackReason.trim()) return;
    setBusyId(rollbackTarget.id);
    try {
      const res = await api.rollbackPromotion(rollbackTarget.id, { reason: rollbackReason.trim() });
      if (res && res.ok === false) {
        toast.addToast({ tone: 'danger', message: res.message || res.error || 'Rollback failed' });
        return;
      }
      toast.addToast({ tone: 'success', message: 'Promotion rolled back' });
      setRollbackTarget(null);
      setRollbackReason('');
      await reload();
    } catch (e) {
      toast.addToast({ tone: 'danger', message: e?.message || 'Rollback failed' });
    } finally {
      setBusyId(null);
    }
  }, [rollbackTarget, rollbackReason, reload, toast]);

  if (loading && rows === null) {
    return <Card><Card.Body><div className="text-[12px] text-v2-ink-muted">Loading promotion history…</div></Card.Body></Card>;
  }
  if (error) {
    return (
      <Card><Card.Body>
        <div className="text-[12px] text-v2-danger border border-v2-danger rounded-v2-md px-3 py-2 bg-v2-danger-soft">{error}</div>
      </Card.Body></Card>
    );
  }

  const isEmpty = !rows || rows.length === 0;
  const statusOptions = ['', 'PENDING_APPROVAL', 'PROMOTED', 'REJECTED', 'FAILED', 'ROLLED_BACK'];

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="text-[13px] font-medium text-v2-ink">Promotion history</div>
          <div className="text-[11.5px] text-v2-ink-muted mt-0.5">
            Every promotion attempted in this tenant, newest first. Use the status filter to focus on what needs attention.
          </div>
        </div>
        <select
          className="v2-focus-ring border border-v2-border rounded-v2-md px-2 py-1 text-[12px] bg-v2-surface"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
        >
          {statusOptions.map(s => (
            <option key={s || 'all'} value={s}>
              {s ? PROMOTION_STATUS_LABEL[s] || s : 'All statuses'}
            </option>
          ))}
        </select>
      </div>

      {isEmpty ? (
        <EmptyState
          icon={<Activity size={20} />}
          title={statusFilter ? `No ${PROMOTION_STATUS_LABEL[statusFilter] || statusFilter} promotions` : 'No promotions yet'}
          description={statusFilter
            ? 'Try a different status filter or clear it to see all promotions.'
            : 'When someone promotes a workflow, agent, connector, or connector group between this tenant\'s instances, the event will appear here with full audit trail.'}
        />
      ) : (
        <Card>
          <Card.Body>
            <table className="w-full text-[12px]">
              <thead className="text-left text-v2-ink-muted text-[10.5px] uppercase tracking-wider">
                <tr>
                  <th className="py-2">When</th>
                  <th>Resource</th>
                  <th>From → To</th>
                  <th>Status</th>
                  <th>Initiator</th>
                  <th>Approver</th>
                  {canRollback && <th aria-label="Actions" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-v2-line">
                {rows.map(p => (
                  <tr key={p.id}>
                    <td className="py-2 whitespace-nowrap text-v2-ink-muted">
                      {p.initiatedAt ? new Date(p.initiatedAt).toLocaleString() : '—'}
                    </td>
                    <td>
                      <div className="font-medium text-v2-ink">{p.sourceResourceName || '(unnamed)'}</div>
                      <div className="text-[10.5px] text-v2-ink-muted">{p.resourceType}</div>
                    </td>
                    <td>
                      <div className="flex items-center gap-1.5">
                        <Pill tone={TIER_TONE[p.sourceInstanceTier] || 'muted'} size="sm">{p.sourceInstanceName}</Pill>
                        <span className="text-v2-ink-muted">→</span>
                        <Pill tone={TIER_TONE[p.targetInstanceTier] || 'muted'} size="sm">{p.targetInstanceName}</Pill>
                      </div>
                    </td>
                    <td>
                      <Pill tone={PROMOTION_STATUS_TONE[p.status] || 'muted'} size="sm">
                        {PROMOTION_STATUS_LABEL[p.status] || p.status}
                      </Pill>
                    </td>
                    <td className="text-v2-ink">{p.initiatedByName || '—'}</td>
                    <td className="text-v2-ink">{p.approvedByName || (p.rolledBackByName ? `↺ ${p.rolledBackByName}` : '—')}</td>
                    {canRollback && (
                      <td className="text-right whitespace-nowrap">
                        {p.status === 'PROMOTED' && (
                          <Button variant="ghost" size="sm" iconLeft={<Trash2 size={12} />}
                                  disabled={busyId === p.id}
                                  className="!text-v2-danger hover:!bg-v2-danger-soft"
                                  onClick={() => { setRollbackTarget(p); setRollbackReason(''); }}>
                            Roll back
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card.Body>
        </Card>
      )}

      {/* Rollback confirm modal — reason is required server-side (400 if missing).
       * Modal enforces the same client-side so the operator gets immediate feedback. */}
      <Modal
        isOpen={!!rollbackTarget}
        onClose={() => { setRollbackTarget(null); setRollbackReason(''); }}
        title="Roll back promotion"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setRollbackTarget(null); setRollbackReason(''); }}>Cancel</Button>
            <Button variant="danger" onClick={handleRollback}
                    disabled={!rollbackReason.trim() || busyId === rollbackTarget?.id}>
              Roll back promotion
            </Button>
          </>
        }
      >
        {rollbackTarget && (
          <div className="space-y-3 text-[13px]">
            <p>
              Roll back the promotion of <strong>{rollbackTarget.sourceResourceName || '(unnamed)'}</strong>
              {' '}from <strong>{rollbackTarget.sourceInstanceName}</strong> to
              {' '}<strong>{rollbackTarget.targetInstanceName}</strong>?
            </p>
            <p className="text-v2-ink-muted text-[12px]">
              The target artifact will be removed. The promotion event row stays in the audit log
              with status flipped to "Rolled back" and the reason you provide below.
            </p>
            <label className="block">
              <div className="text-[11.5px] uppercase tracking-wider text-v2-ink-muted mb-1">Reason (required)</div>
              <textarea
                autoFocus
                className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2 py-1.5 text-[13px] bg-v2-surface"
                rows={3}
                value={rollbackReason}
                onChange={(e) => setRollbackReason(e.target.value)}
                placeholder="Why are you rolling this back? Surfaces in the audit log."
              />
            </label>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// TenantDetailPanel
// ──────────────────────────────────────────────────────────────────────
function TenantDetailPanel({ tenant, instances: instancesProp, onEdit, onDelete, canEdit, canDelete }) {
  const instances = Array.isArray(instancesProp) ? instancesProp : [];
  const [tab, setTab] = useState('overview');

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Layers size={18} className="text-v2-ink-muted" />
        <h2 className="text-[16px] font-semibold text-v2-ink flex-1">{tenant.name}</h2>
        <Pill tone={STATUS_TONE[(tenant.status || 'ACTIVE').toUpperCase()] || 'muted'} size="sm">
          {(tenant.status || 'active').toLowerCase()}
        </Pill>
        {canEdit && (
          <Button variant="ghost" size="sm" iconLeft={<Edit2 size={12} />} onClick={() => onEdit && onEdit(tenant)}>Edit</Button>
        )}
        {canDelete && (
          <Button variant="ghost" size="sm" iconLeft={<Trash2 size={12} />} onClick={() => onDelete && onDelete(tenant)}
                  className="!text-v2-danger hover:!bg-v2-danger-soft">Delete</Button>
        )}
      </div>

      <TabNav
        tabs={[
          { id: 'overview',   label: 'Overview' },
          { id: 'instances',  label: `Instances (${instances.length})` },
          { id: 'promotion',  label: 'Promotion Graph' },
          /* Phase 9 — newest-first history of every promotion event under
           * this tenant. Status filter + rollback affordance on PROMOTED
           * rows. The Promotion Graph (above) declares what's ALLOWED;
           * this Promotions tab shows what's HAPPENED. */
          { id: 'promotions', label: 'Promotions' },
          { id: 'members',    label: 'Members' },
        ]}
        activeId={tab}
        onChange={setTab}
      />

      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <MetricCard label="Instances" value={instances.length} />
            <MetricCard
              label="Production"
              value={instances.filter(i => i.isProduction).length}
              tone={instances.some(i => i.isProduction) ? 'danger' : 'muted'}
            />
            <MetricCard label="Status" value={tenant.status} />
          </div>
          <Card>
            <Card.Header title="Identifiers" />
            <Card.Body>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-[12px]">
                <dt className="text-v2-ink-muted">Tenant ID</dt>
                <dd><CopyId value={tenant.id} /></dd>
                {tenant.orgId && (
                  <>
                    <dt className="text-v2-ink-muted">Org ID</dt>
                    <dd><CopyId value={tenant.orgId} /></dd>
                  </>
                )}
              </dl>
            </Card.Body>
          </Card>
        </div>
      )}

      {tab === 'instances' && (
        <Card>
          <Card.Body>
            {instances.length === 0 ? (
              <EmptyState icon={<Boxes size={20} />} title="No instances yet" />
            ) : (
              <table className="w-full text-[12px]">
                <thead className="text-left text-v2-ink-muted text-[10.5px] uppercase tracking-wider">
                  <tr>
                    <th className="py-2">Name</th>
                    <th>Tier</th>
                    <th>Production</th>
                    <th>Status</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-v2-line">
                  {instances.map(i => (
                    <tr key={i.id}>
                      <td className="py-2">
                        <div className="font-medium text-v2-ink">{i.name}</div>
                        <div className="text-[10.5px] text-v2-ink-muted font-mono-v2">{i.id}</div>
                      </td>
                      <td><Pill tone={TIER_TONE[i.tier] || 'muted'} size="sm">{i.tier}</Pill></td>
                      <td>
                        {i.isProduction
                          ? <Pill tone="danger" size="sm" dot>Yes</Pill>
                          : <span className="text-v2-ink-muted">No</span>}
                      </td>
                      <td><Pill tone={STATUS_TONE[i.status] || 'muted'} size="sm">{i.status}</Pill></td>
                      <td className="text-v2-ink-muted">
                        {i.updatedAt ? new Date(i.updatedAt).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card.Body>
        </Card>
      )}

      {tab === 'promotion' && (
        /* G552 — real DAG visualization + author tools (replaces the
         * V191/Phase-3 placeholder). canEdit is the same gate used for
         * the Edit/Delete tenant controls; matches tenant.edit perm. */
        <TenantPromotionGraphView
          tenant={tenant}
          instances={instances}
          canEdit={canEdit}
        />
      )}

      {tab === 'promotions' && (
        /* Phase 9 — newest-first history of every promotion event under
         * this tenant. canEdit gates the Rollback button on PROMOTED rows
         * (same gate as Edit Tenant — matches promotion.rollback which
         * Tenant Admin + Org Admin both hold). */
        <TenantPromotionsView tenantId={tenant.id} canRollback={canEdit} />
      )}

      {tab === 'members' && (
        <MembersList kind="tenant" scopeId={tenant.id} />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// InstanceDetailPanel
// ──────────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────
// V191 / Phase-7 — Lineage view inside the Instance detail panel.
//
// Renders three projections in stacked cards:
//   1. Fork chain  — every ancestor of this Instance (walks promoted_from_*).
//   2. Forks       — direct descendants (every Instance whose
//                    promoted_from_instance_id equals this Instance).
//   3. Promotion events — recent PromotionEvent rows targeting this
//                         Instance, newest-first, capped at 50.
// All three render purposeful empty states when no data is present
// (common today; populated by Phase-9 promote flows).
// ──────────────────────────────────────────────────────────────────────
function InstanceLineageView({ instance, lineage, loading, error }) {
  if (loading) {
    return (
      <Card><Card.Body>
        <div className="text-[12px] text-v2-ink-muted">Loading lineage…</div>
      </Card.Body></Card>
    );
  }
  if (error) {
    return (
      <Card><Card.Body>
        <div className="text-[12px] text-v2-danger">Failed to load lineage: {String(error)}</div>
      </Card.Body></Card>
    );
  }
  const forkChain = Array.isArray(lineage?.forkChain) ? lineage.forkChain : [];
  const forks = Array.isArray(lineage?.forks) ? lineage.forks : [];
  const events = Array.isArray(lineage?.promotionEvents) ? lineage.promotionEvents : [];

  const EVENT_STATUS_TONE = {
    PENDING_APPROVAL: 'warning',
    APPROVED:         'cyan',
    REJECTED:         'danger',
    PROMOTED:         'success',
    FAILED:           'danger',
    ROLLED_BACK:      'muted',
  };

  return (
    <div className="space-y-4">
      {/* ── Fork chain (ancestors) ───────────────────────────────── */}
      <Card>
        <Card.Header title="Forked-from chain"
                     subtitle="Ancestors walked upward via promoted_from_instance_id." />
        <Card.Body>
          {forkChain.length === 0 ? (
            <div className="text-[12px] text-v2-ink-muted italic">
              No ancestors — this is a root Instance.
            </div>
          ) : (
            <ol className="space-y-1.5">
              {forkChain.map((anc, idx) => (
                <li key={anc.id} className="flex items-center gap-2 text-[12px]">
                  <span className="text-v2-ink-muted font-mono-v2 text-[10.5px] w-6 text-right">
                    {idx === 0 ? 'parent' : `+${idx + 1}`}
                  </span>
                  <Boxes size={13} className="text-v2-ink-muted" />
                  <span className="font-medium text-v2-ink">{anc.name}</span>
                  <Pill tone={TIER_TONE[anc.tier] || 'muted'} size="sm">{anc.tier}</Pill>
                  {anc.isProduction && <Pill tone="danger" size="sm" dot>Production</Pill>}
                  <span className="text-[10.5px] text-v2-ink-muted font-mono-v2 ml-auto">{anc.id.slice(0, 8)}…</span>
                </li>
              ))}
              <li className="flex items-center gap-2 text-[12px] pt-1 border-t border-v2-line">
                <span className="text-v2-ink-muted font-mono-v2 text-[10.5px] w-6 text-right">here</span>
                <Boxes size={13} className="text-v2-cyan" />
                <span className="font-semibold text-v2-cyan">{instance.name}</span>
                <Pill tone={TIER_TONE[instance.tier] || 'muted'} size="sm">{instance.tier}</Pill>
                {instance.isProduction && <Pill tone="danger" size="sm" dot>Production</Pill>}
              </li>
            </ol>
          )}
        </Card.Body>
      </Card>

      {/* ── Forks (descendants) ──────────────────────────────────── */}
      <Card>
        <Card.Header title={`Forks (${forks.length})`}
                     subtitle="Instances whose promoted_from_instance_id points at this one." />
        <Card.Body>
          {forks.length === 0 ? (
            <div className="text-[12px] text-v2-ink-muted italic">
              No descendants yet. Forked instances will appear here.
            </div>
          ) : (
            <ul className="divide-y divide-v2-line">
              {forks.map((f) => (
                <li key={f.id} className="py-2 flex items-center gap-2 text-[12px]">
                  <Boxes size={13} className="text-v2-ink-muted" />
                  <span className="font-medium text-v2-ink">{f.name}</span>
                  <Pill tone={TIER_TONE[f.tier] || 'muted'} size="sm">{f.tier}</Pill>
                  <Pill tone={STATUS_TONE[f.status] || 'muted'} size="sm">{f.status}</Pill>
                  <span className="text-[10.5px] text-v2-ink-muted font-mono-v2 ml-auto">{f.id.slice(0, 8)}…</span>
                </li>
              ))}
            </ul>
          )}
        </Card.Body>
      </Card>

      {/* ── Promotion events targeting this Instance ─────────────── */}
      <Card>
        <Card.Header title={`Promotion events (${events.length})`}
                     subtitle="Recent promotion attempts into this Instance, newest first. Capped at 50." />
        <Card.Body>
          {events.length === 0 ? (
            <div className="text-[12px] text-v2-ink-muted italic">
              No promotion events yet. Promotions into this instance will appear here.
            </div>
          ) : (
            <table className="w-full text-[12px]">
              <thead className="text-left text-v2-ink-muted text-[10.5px] uppercase tracking-wider">
                <tr>
                  <th className="py-2">When</th>
                  <th>Resource</th>
                  <th>From</th>
                  <th>Status</th>
                  <th>Initiated by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-v2-line">
                {events.map((e) => (
                  <tr key={e.id}>
                    <td className="py-2 text-v2-ink-muted">
                      {e.initiatedAt ? new Date(e.initiatedAt).toLocaleString() : '—'}
                    </td>
                    <td>
                      <div className="font-medium text-v2-ink">{e.resourceType}</div>
                      <div className="text-[10.5px] text-v2-ink-muted font-mono-v2">{e.sourceResourceId?.slice(0, 8)}…</div>
                    </td>
                    <td className="text-v2-ink-muted">
                      <span className="font-mono-v2 text-[10.5px]">{e.sourceInstanceId?.slice(0, 8)}…</span>
                    </td>
                    <td>
                      <Pill tone={EVENT_STATUS_TONE[e.status] || 'muted'} size="sm">{e.status}</Pill>
                    </td>
                    <td className="text-v2-ink-muted font-mono-v2 text-[10.5px]">
                      {e.initiatedByUserId?.slice(0, 8)}…
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

function InstanceDetailPanel({ instance, onEdit, onArchive, canEdit, canArchive }) {
  const [tab, setTab] = useState('overview');

  // V191 / Phase-7 — lazy-load Lineage payload only when the Lineage
  // tab is first visited. Cached per Instance for the panel lifetime.
  const [lineage, setLineage] = useState(null);
  const [lineageLoading, setLineageLoading] = useState(false);
  const [lineageError, setLineageError] = useState(null);

  /* G550 — lineageLoading was previously in the dependency array which
   * caused a classic race: setLineageLoading(true) re-fired the effect,
   * cleanup of the original run flipped `cancelled = true`, then the
   * resolved fetch hit `if (cancelled) return;` and skipped BOTH
   * setLineage and the `finally`-guarded setLineageLoading(false). Net
   * result: spinner stuck at "Loading lineage…" forever even though the
   * backend returned 200. Fix is to depend only on the inputs that
   * SELECT which lineage to fetch (tab + instance.id) plus `lineage`
   * itself so the cached payload short-circuits re-entry. */
  useEffect(() => {
    if (tab !== 'lineage' || lineage) return;
    let cancelled = false;
    (async () => {
      setLineageLoading(true);
      setLineageError(null);
      try {
        const res = await api.getInstanceLineage(instance.id);
        if (cancelled) return;
        if (res && res.ok === false) {
          setLineageError(res.error || 'Failed to load lineage');
          setLineage(null);
        } else {
          const payload = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
          setLineage(payload || {});
        }
      } catch (err) {
        if (!cancelled) setLineageError(err?.message || String(err));
      } finally {
        if (!cancelled) setLineageLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tab, instance.id, lineage]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Boxes size={18} className="text-v2-ink-muted" />
        <h2 className="text-[16px] font-semibold text-v2-ink">{instance.name}</h2>
        <Pill tone={TIER_TONE[instance.tier] || 'muted'} size="sm">{instance.tier}</Pill>
        {instance.isProduction && <Pill tone="danger" size="sm" dot>Production</Pill>}
        <Pill tone={STATUS_TONE[instance.status] || 'muted'} size="sm">{instance.status}</Pill>
        <div className="flex-1" />
        {canEdit && (
          <Button variant="ghost" size="sm" iconLeft={<Edit2 size={12} />} onClick={() => onEdit && onEdit(instance)}>Edit</Button>
        )}
        {canArchive && instance.status !== 'ARCHIVED' && (
          <Button variant="ghost" size="sm" iconLeft={<Trash2 size={12} />} onClick={() => onArchive && onArchive(instance)}
                  className="!text-v2-warning hover:!bg-v2-warning-soft">Archive</Button>
        )}
      </div>

      <TabNav
        tabs={[
          { id: 'overview',  label: 'Overview' },
          { id: 'resources', label: 'Resources' },
          { id: 'members',   label: 'Members' },
          { id: 'lineage',   label: 'Lineage' },
          { id: 'audit',     label: 'Audit' },
        ]}
        activeId={tab}
        onChange={setTab}
      />

      {tab === 'overview' && (
        <Card>
          <Card.Body>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-[12px]">
              <dt className="text-v2-ink-muted">Instance ID</dt>
              <dd><CopyId value={instance.id} /></dd>

              <dt className="text-v2-ink-muted">Tenant ID</dt>
              <dd><CopyId value={instance.tenantId} /></dd>

              <dt className="text-v2-ink-muted">Tier</dt>
              <dd><Pill tone={TIER_TONE[instance.tier] || 'muted'} size="sm">{instance.tier}</Pill></dd>

              <dt className="text-v2-ink-muted">is_production</dt>
              <dd>{instance.isProduction ? 'Yes' : 'No'}</dd>

              <dt className="text-v2-ink-muted">Requires promotion approval</dt>
              <dd>{instance.requiresPromotionApproval ? 'Yes' : 'No'}</dd>

              <dt className="text-v2-ink-muted">Approver role</dt>
              <dd>{instance.approverRole || <span className="text-v2-ink-muted">— (any user with promotion.approve)</span>}</dd>

              <dt className="text-v2-ink-muted">Status</dt>
              <dd><Pill tone={STATUS_TONE[instance.status] || 'muted'} size="sm">{instance.status}</Pill></dd>

              <dt className="text-v2-ink-muted">Promoted from</dt>
              <dd>{instance.promotedFromInstanceId
                ? <span className="font-mono-v2 text-[11px]">{instance.promotedFromInstanceId}</span>
                : <span className="text-v2-ink-muted">— (root instance)</span>}</dd>

              <dt className="text-v2-ink-muted">Created</dt>
              <dd>{instance.createdAt ? new Date(instance.createdAt).toLocaleString() : '—'}</dd>

              <dt className="text-v2-ink-muted">Updated</dt>
              <dd>{instance.updatedAt ? new Date(instance.updatedAt).toLocaleString() : '—'}</dd>

              {instance.description && (<>
                <dt className="text-v2-ink-muted">Description</dt>
                <dd className="col-span-1">{instance.description}</dd>
              </>)}
            </dl>
          </Card.Body>
        </Card>
      )}

      {tab === 'resources' && (
        <InstanceResourcesView instanceId={instance.id} />
      )}

      {tab === 'members' && (
        <MembersList
          kind="instance"
          scopeId={instance.id}
          scopeHint="Users with an Instance Admin grant or other instance-scoped role for this instance will be listed here."
        />
      )}

      {tab === 'lineage' && (
        <InstanceLineageView
          instance={instance}
          lineage={lineage}
          loading={lineageLoading}
          error={lineageError}
        />
      )}

      {tab === 'audit' && (
        <InstanceAuditView instanceId={instance.id} />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────
// V191 / Phase-5b-fix (G523) — Confirm-Delete dialog.
//
// Reusable across Org / Tenant / Instance destructive actions.
// Requires the user to type the exact resource name to confirm —
// prevents accidental cascade deletes. Tone is set by scope:
//   - Instance archive → "Archive" verb (soft-delete, reversible)
//   - Tenant/Org delete → "Delete" verb (HARD delete, irreversible,
//     cascades to descendants per FK CASCADE rules)
// ──────────────────────────────────────────────────────────────────────
function ConfirmDeleteDialog({ isOpen, onClose, kind, name, onConfirm, isSoft }) {
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { if (isOpen) { setTyped(''); setSubmitting(false); setErr(null); } }, [isOpen]);

  const verb = isSoft ? 'Archive' : 'Delete';
  const verbingTitle = isSoft ? 'Archive' : 'Delete';
  const canConfirm = typed.trim() === (name || '');

  const handle = async () => {
    if (!canConfirm) return;
    setSubmitting(true); setErr(null);
    try {
      const res = await onConfirm();
      if (res && res.ok === false) {
        setErr(res.message || res.error || `${verb} failed`);
        setSubmitting(false);
        return;
      }
      onClose();
    } catch (e) {
      setErr(e?.message || String(e));
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`${verbingTitle} ${kind}`}>
      <div className="space-y-3 text-[12.5px]">
        <p className="text-v2-ink">
          {isSoft ? (
            <>This will <strong>archive</strong> {kind.toLowerCase()} <strong>{name}</strong>.
            Archived rows are excluded from active lists but data is preserved and the action is reversible.</>
          ) : (
            <>This will <strong>permanently delete</strong> {kind.toLowerCase()} <strong>{name}</strong> and
            cascade to every dependent row per the FK rules. <span className="text-v2-danger font-semibold">This action cannot be undone.</span></>
          )}
        </p>
        <p className="text-v2-ink-muted">Type <code className="bg-v2-stone-100 px-1 rounded font-mono-v2">{name}</code> to confirm:</p>
        <input
          type="text" value={typed} onChange={(e) => setTyped(e.target.value)}
          className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink font-mono-v2 text-[12px]"
          disabled={submitting}
          autoFocus
        />
        {err && (
          <div className="text-[12px] text-v2-danger border border-v2-danger rounded-v2-md px-3 py-2 bg-v2-danger-soft">{err}</div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant={isSoft ? 'secondary' : 'danger'} onClick={handle}
                  disabled={!canConfirm || submitting}>
            {submitting ? (isSoft ? 'Archiving…' : 'Deleting…') : `${verb} ${kind}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────────────────
// V191 / Phase-5b-fix (G523) — Edit Org Slideout.
// ──────────────────────────────────────────────────────────────────────
function EditOrgSlideout({ isOpen, onClose, org, onSaved }) {
  const { addToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const inFlight = useRef(false);

  useEffect(() => {
    if (isOpen && org) {
      setFormError(null);
      setName(org.name || '');
      setDomain(org.domain || '');
      inFlight.current = false;
    }
  }, [isOpen, org]);

  const submit = async () => {
    if (inFlight.current || !org) return;
    inFlight.current = true;
    setFormError(null);
    if (!name.trim()) { setFormError('Name is required'); inFlight.current = false; return; }
    setSubmitting(true);
    try {
      const res = await api.updateOrganization(org.id, { name: name.trim(), domain: domain.trim() || null });
      if (res && res.ok === false) { setFormError(res.message || res.error || 'Save failed'); return; }
      const saved = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
      addToast({ tone: 'success', title: 'Organization updated', message: `${saved?.name || org.name} saved.` });
      onSaved && onSaved(saved);
      onClose();
    } catch (err) { setFormError(err?.message || String(err)); }
    finally { setSubmitting(false); inFlight.current = false; }
  };

  return (
    <Slideout isOpen={isOpen} onClose={onClose} header={`Edit ${org?.name || 'Organization'}`} ariaLabel="Edit Organization">
      <div className="space-y-4 p-1 text-[12.5px]">
        {formError && <div className="text-[12px] text-v2-danger border border-v2-danger rounded-v2-md px-3 py-2 bg-v2-danger-soft">{formError}</div>}
        <label className="block">
          <span className="block text-v2-ink font-medium mb-1">Name <span className="text-v2-danger">*</span></span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={255}
                 className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink" disabled={submitting} />
        </label>
        <label className="block">
          <span className="block text-v2-ink font-medium mb-1">Domain</span>
          <input type="text" value={domain} onChange={(e) => setDomain(e.target.value)}
                 className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink" disabled={submitting} />
        </label>
        {org?.id && (
          <div className="pt-3 border-t border-v2-line">
            <span className="block text-v2-ink font-medium mb-2">Members</span>
            <ScopeMemberManager kind="org" orgId={org.id} defaultRole="Org Admin" />
          </div>
        )}
        <div className="flex justify-end gap-2 pt-4 border-t border-v2-line">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={submitting || !name.trim()}>
            {submitting ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </Slideout>
  );
}

// ──────────────────────────────────────────────────────────────────────
// V191 / Phase-5b-fix (G523) — Edit Tenant Slideout.
// ──────────────────────────────────────────────────────────────────────
function EditTenantSlideout({ isOpen, onClose, tenant, onSaved }) {
  const { addToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [name, setName] = useState('');
  const [status, setStatus] = useState('active');
  const inFlight = useRef(false);
  // V195 — environment field removed. Env semantics live on Instance.tier
  // + Instance.is_production. The backend silently normalizes the column
  // to 'multi' for every Tenant.

  useEffect(() => {
    if (isOpen && tenant) {
      setFormError(null);
      setName(tenant.name || '');
      setStatus(tenant.status || 'active');
      inFlight.current = false;
    }
  }, [isOpen, tenant]);

  const submit = async () => {
    if (inFlight.current || !tenant) return;
    inFlight.current = true;
    setFormError(null);
    if (!name.trim()) { setFormError('Name is required'); inFlight.current = false; return; }
    setSubmitting(true);
    try {
      const res = await api.updateTenant(tenant.id, { name: name.trim(), status });
      if (res && res.ok === false) { setFormError(res.message || res.error || 'Save failed'); return; }
      const saved = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
      addToast({ tone: 'success', title: 'Tenant updated', message: `${saved?.name || tenant.name} saved.` });
      onSaved && onSaved(saved);
      onClose();
    } catch (err) { setFormError(err?.message || String(err)); }
    finally { setSubmitting(false); inFlight.current = false; }
  };

  return (
    <Slideout isOpen={isOpen} onClose={onClose} header={`Edit ${tenant?.name || 'Tenant'}`} ariaLabel="Edit Tenant">
      <div className="space-y-4 p-1 text-[12.5px]">
        {formError && <div className="text-[12px] text-v2-danger border border-v2-danger rounded-v2-md px-3 py-2 bg-v2-danger-soft">{formError}</div>}
        <label className="block">
          <span className="block text-v2-ink font-medium mb-1">Name <span className="text-v2-danger">*</span></span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={255}
                 className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink" disabled={submitting} />
        </label>
        <label className="block">
          <span className="block text-v2-ink font-medium mb-1">Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
                  className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink" disabled={submitting}>
            <option value="active">active</option>
            <option value="paused">paused</option>
            <option value="archived">archived</option>
          </select>
        </label>
        {tenant?.id && (
          <div className="pt-3 border-t border-v2-line">
            <span className="block text-v2-ink font-medium mb-2">Members</span>
            <ScopeMemberManager kind="tenant" orgId={tenant.orgId} tenantId={tenant.id} defaultRole="Tenant Admin" />
          </div>
        )}
        <div className="flex justify-end gap-2 pt-4 border-t border-v2-line">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={submitting || !name.trim()}>
            {submitting ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </Slideout>
  );
}

// ──────────────────────────────────────────────────────────────────────
// V191 / Phase-5b-fix (G523) — Edit Instance Slideout.
// ──────────────────────────────────────────────────────────────────────
function EditInstanceSlideout({ isOpen, onClose, instance, onSaved }) {
  const { addToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [name, setName] = useState('');
  const [tier, setTier] = useState('CUSTOM');
  const [isProduction, setIsProduction] = useState(false);
  const [requiresPromotionApproval, setRequiresPromotionApproval] = useState(false);
  const [approverRole, setApproverRole] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  // Metering/pricing dimensions (V292) — base fee by size, mode (or derived from a linked target).
  const [size, setSize] = useState('');
  const [deploymentMode, setDeploymentMode] = useState('prove7_hosted');
  const [deploymentTargetId, setDeploymentTargetId] = useState('');
  const [targets, setTargets] = useState([]);
  const inFlight = useRef(false);

  useEffect(() => {
    if (isOpen && instance) {
      setFormError(null);
      setName(instance.name || '');
      setTier(instance.tier || 'CUSTOM');
      setIsProduction(!!instance.isProduction);
      setRequiresPromotionApproval(!!instance.requiresPromotionApproval);
      setApproverRole(instance.approverRole || '');
      setDescription(instance.description || '');
      setStatus(instance.status || 'ACTIVE');
      setSize(instance.size || '');
      setDeploymentMode(instance.deploymentMode || 'prove7_hosted');
      setDeploymentTargetId(instance.deploymentTargetId || '');
      inFlight.current = false;
    }
  }, [isOpen, instance]);

  // Deployment targets populate the link dropdown; best-effort (PA/SA readable).
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.listDeploymentTargets();
        const data = res && res.data ? res.data : res;
        const items = (data && Array.isArray(data.items)) ? data.items : (Array.isArray(data) ? data : []);
        if (!cancelled) setTargets(items);
      } catch (_) { /* best-effort — mode stays manually editable */ }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  const submit = async () => {
    if (inFlight.current || !instance) return;
    inFlight.current = true;
    setFormError(null);
    if (!name.trim()) { setFormError('Name is required'); inFlight.current = false; return; }
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(), tier, isProduction, requiresPromotionApproval,
        approverRole: approverRole.trim() || null,
        description: description.trim() || null,
        status,
      };
      // Pricing dimensions. Size sent only when set (no clear-to-null in v1).
      if (size) payload.size = size;
      // A linked target is the single source of truth for mode — send the link, let the server
      // derive the mode. Only un-linked instances may set an explicit mode (matches the backend).
      if (deploymentTargetId) {
        payload.deploymentTargetId = deploymentTargetId;
      } else if (!instance.deploymentTargetId) {
        payload.deploymentMode = deploymentMode;
      }
      const res = await api.updateInstance(instance.id, payload);
      if (res && res.ok === false) { setFormError(res.message || res.error || 'Save failed'); return; }
      const saved = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
      addToast({ tone: 'success', title: 'Instance updated', message: `${saved?.name || instance.name} saved.` });
      onSaved && onSaved(saved);
      onClose();
    } catch (err) { setFormError(err?.message || String(err)); }
    finally { setSubmitting(false); inFlight.current = false; }
  };

  return (
    <Slideout isOpen={isOpen} onClose={onClose} header={`Edit ${instance?.name || 'Instance'}`} ariaLabel="Edit Instance">
      <div className="space-y-4 p-1 text-[12.5px]">
        {formError && <div className="text-[12px] text-v2-danger border border-v2-danger rounded-v2-md px-3 py-2 bg-v2-danger-soft">{formError}</div>}
        <label className="block">
          <span className="block text-v2-ink font-medium mb-1">Name <span className="text-v2-danger">*</span></span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
                 className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink" disabled={submitting} />
        </label>
        <label className="block">
          <span className="block text-v2-ink font-medium mb-1">Tier</span>
          <select value={tier} onChange={(e) => setTier(e.target.value)}
                  className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink" disabled={submitting}>
            <option value="PRODUCTION">PRODUCTION</option>
            <option value="STAGING">STAGING</option>
            <option value="DEV">DEV</option>
            <option value="SYSTEM">SYSTEM</option>
            <option value="CUSTOM">CUSTOM</option>
          </select>
        </label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-v2-ink">
            <input type="checkbox" checked={isProduction} onChange={(e) => setIsProduction(e.target.checked)} disabled={submitting} />
            <span>Is production</span>
          </label>
          <label className="flex items-center gap-2 text-v2-ink">
            <input type="checkbox" checked={requiresPromotionApproval} onChange={(e) => setRequiresPromotionApproval(e.target.checked)} disabled={submitting} />
            <span>Requires promotion approval</span>
          </label>
        </div>
        <label className="block">
          <span className="block text-v2-ink font-medium mb-1">Approver role</span>
          <input type="text" value={approverRole} onChange={(e) => setApproverRole(e.target.value)}
                 className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink" disabled={submitting} />
        </label>
        <label className="block">
          <span className="block text-v2-ink font-medium mb-1">Description</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
                    className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink" disabled={submitting} />
        </label>
        <label className="block">
          <span className="block text-v2-ink font-medium mb-1">Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
                  className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink" disabled={submitting}>
            <option value="ACTIVE">ACTIVE</option>
            <option value="PAUSED">PAUSED</option>
            <option value="ARCHIVED">ARCHIVED</option>
          </select>
        </label>

        {/* Pricing & deployment (metering dimensions) */}
        <div className="pt-3 border-t border-v2-line space-y-4">
          <div className="text-v2-ink font-semibold text-[12px]">Pricing &amp; deployment</div>
          <label className="block">
            <span className="block text-v2-ink font-medium mb-1">Compute size</span>
            <select value={size} onChange={(e) => setSize(e.target.value)}
                    className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink" disabled={submitting}>
              <option value="">— Unprovisioned —</option>
              {['XS', 'S', 'M', 'LG', 'XL'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="block text-[11px] text-v2-ink-muted mt-1">Drives the instance base fee on the invoice.</span>
          </label>
          <label className="block">
            <span className="block text-v2-ink font-medium mb-1">Deployment target</span>
            <select value={deploymentTargetId}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDeploymentTargetId(v);
                      if (v) { const t = targets.find((x) => x.id === v); if (t?.mode) setDeploymentMode(t.mode); }
                    }}
                    className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink" disabled={submitting}>
              <option value="">— None (set mode manually) —</option>
              {deploymentTargetId && !targets.some((t) => t.id === deploymentTargetId) && (
                <option value={deploymentTargetId}>Linked target ({String(deploymentTargetId).slice(0, 8)}…)</option>
              )}
              {targets.map((t) => (
                <option key={t.id} value={t.id}>{t.name} · {t.mode === 'self_hosted' ? 'Self-Hosted' : 'Prove7 Cloud'}</option>
              ))}
            </select>
            <span className="block text-[11px] text-v2-ink-muted mt-1">
              Linking derives the deployment mode from the target. {instance?.deploymentTargetId ? 'Unlinking isn’t available yet.' : ''}
            </span>
          </label>
          <label className="block">
            <span className="block text-v2-ink font-medium mb-1">Deployment mode</span>
            <select value={deploymentMode} onChange={(e) => setDeploymentMode(e.target.value)}
                    className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink"
                    disabled={submitting || !!deploymentTargetId}>
              <option value="prove7_hosted">Prove7 Cloud</option>
              <option value="self_hosted">Self-Hosted</option>
            </select>
            <span className="block text-[11px] text-v2-ink-muted mt-1">
              {deploymentTargetId
                ? 'Derived from the linked target. Self-Hosted bills at Prove7 Cloud × 0.80.'
                : 'Self-Hosted bills at Prove7 Cloud × 0.80.'}
            </span>
          </label>
        </div>

        {instance?.id && (
          <div className="pt-3 border-t border-v2-line">
            <span className="block text-v2-ink font-medium mb-2">Members</span>
            <ScopeMemberManager kind="instance" tenantId={instance.tenantId} instanceId={instance.id} defaultRole="Instance Admin" />
          </div>
        )}
        <div className="flex justify-end gap-2 pt-4 border-t border-v2-line">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={submitting || !name.trim()}>
            {submitting ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </Slideout>
  );
}

// ──────────────────────────────────────────────────────────────────────
// V191 / G527 — AdminAssignStep
//
// Reusable optional admin-assignment subsection used by all three Create
// slideouts (Org / Tenant / Instance). Two modes:
//
//   Mode A: Existing user — type email; live search against
//     /api/v1/team/users; pick from match list. The chosen user gets a
//     UserOrgRole grant at the target scope via api.addUserMembership.
//
//   Mode B: Invite by email — type first name, last name, email. Reuses
//     the V1 invite path: POST /api/v1/team/users with status='invited'.
//     TeamService auto-generates a 24-hour token + creates the default
//     UserOrgRole grant. If the target is instance-scoped, the parent
//     Create handler issues a second addUserMembership call to add the
//     instance-scoped grant (the auto-grant is tenant-wide).
//
// Controlled component — parent owns the state and the orchestration
// (after resource create, call onAssign(state) which returns a Promise).
//
// Returns from onAssign:
//   { mode: 'none' }                                  // checkbox unchecked
//   { mode: 'existing', userId, role }
//   { mode: 'invite', firstName, lastName, email, role }
// ──────────────────────────────────────────────────────────────────────
function AdminAssignStep({
  enabled, onEnabledChange,
  mode, onModeChange,
  userQuery, onUserQueryChange,
  pickedUser, onPickedUserChange,
  firstName, onFirstNameChange,
  lastName, onLastNameChange,
  email, onEmailChange,
  role, onRoleChange,
  availableRoles,
  disabled,
}) {
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Debounced lookup of /api/v1/team/users filtered by email substring.
  useEffect(() => {
    if (!enabled || mode !== 'existing' || !userQuery || userQuery.length < 2) {
      setSearchResults([]); return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.getUsers();
        if (cancelled) return;
        const payload = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
        const list = Array.isArray(payload) ? payload : [];
        const q = userQuery.toLowerCase();
        setSearchResults(list.filter(u =>
          (u.email || '').toLowerCase().includes(q) ||
          ((u.firstName || '') + ' ' + (u.lastName || '')).toLowerCase().includes(q)
        ).slice(0, 8));
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [enabled, mode, userQuery]);

  if (!enabled) {
    return (
      <div className="border-t border-v2-line pt-3">
        <label className="flex items-center gap-2 text-v2-ink font-medium">
          <input type="checkbox" checked={false} onChange={(e) => onEnabledChange(e.target.checked)} disabled={disabled} />
          <span>Assign admin now</span>
        </label>
        <span className="block text-[10.5px] text-v2-ink-muted mt-0.5">Atomically grants the role on this new scope, or invites a new user via the V1 invite path.</span>
      </div>
    );
  }

  return (
    <div className="border-t border-v2-line pt-3 space-y-3">
      <label className="flex items-center gap-2 text-v2-ink font-medium">
        <input type="checkbox" checked={true} onChange={(e) => onEnabledChange(e.target.checked)} disabled={disabled} />
        <span>Assign admin now</span>
      </label>

      {/* Mode tabs */}
      <div className="flex gap-2 text-[12px]">
        <button type="button"
                onClick={() => onModeChange('existing')}
                className={`px-3 py-1 rounded-v2-sm border ${mode === 'existing' ? 'bg-v2-cyan-soft text-v2-cyan border-v2-cyan' : 'bg-white text-v2-ink-muted border-v2-line hover:border-v2-ink-muted'}`}>
          Existing user
        </button>
        <button type="button"
                onClick={() => onModeChange('invite')}
                className={`px-3 py-1 rounded-v2-sm border ${mode === 'invite' ? 'bg-v2-cyan-soft text-v2-cyan border-v2-cyan' : 'bg-white text-v2-ink-muted border-v2-line hover:border-v2-ink-muted'}`}>
          Invite by email
        </button>
      </div>

      {mode === 'existing' ? (
        <>
          <label className="block">
            <span className="block text-v2-ink font-medium mb-1">Find user <span className="text-v2-danger">*</span></span>
            <input
              type="text" value={userQuery}
              onChange={(e) => { onUserQueryChange(e.target.value); onPickedUserChange(null); }}
              placeholder="Type email or name (min 2 chars)…"
              className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink text-[12px]"
              disabled={disabled}
            />
          </label>
          {pickedUser ? (
            <div className="flex items-center gap-2 text-[12px] bg-v2-cyan-soft text-v2-cyan border border-v2-cyan rounded-v2-md px-3 py-2">
              <span className="font-semibold">Selected:</span>
              <span>{pickedUser.firstName || ''} {pickedUser.lastName || ''}</span>
              <span className="text-v2-ink-muted">·</span>
              <span className="font-mono-v2 text-[11px]">{pickedUser.email}</span>
              <button type="button" className="ml-auto text-v2-ink-muted hover:text-v2-ink"
                      onClick={() => onPickedUserChange(null)}>Change</button>
            </div>
          ) : userQuery && userQuery.length >= 2 ? (
            searching ? (
              <div className="text-[11px] text-v2-ink-muted">Searching…</div>
            ) : searchResults.length === 0 ? (
              <div className="text-[11px] text-v2-ink-muted italic">
                No match. Switch to <button type="button" className="underline" onClick={() => onModeChange('invite')}>Invite by email</button>?
              </div>
            ) : (
              <ul className="border border-v2-line rounded-v2-md divide-y divide-v2-line">
                {searchResults.map(u => (
                  <li key={u.id}>
                    <button type="button"
                            className="w-full text-left px-3 py-2 hover:bg-v2-stone-100 text-[12px]"
                            onClick={() => onPickedUserChange(u)}>
                      <div className="font-medium text-v2-ink">{u.firstName || ''} {u.lastName || ''} <span className="text-v2-ink-muted font-normal">{u.email}</span></div>
                      <div className="text-[10.5px] text-v2-ink-muted">role: {u.role || 'Viewer'} · id: {u.id.slice(0, 8)}…</div>
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : null}
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="block text-v2-ink font-medium mb-1 text-[11px]">First name <span className="text-v2-danger">*</span></span>
              <input type="text" value={firstName} onChange={(e) => onFirstNameChange(e.target.value)}
                     className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink text-[12px]" disabled={disabled} />
            </label>
            <label className="block">
              <span className="block text-v2-ink font-medium mb-1 text-[11px]">Last name</span>
              <input type="text" value={lastName} onChange={(e) => onLastNameChange(e.target.value)}
                     className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink text-[12px]" disabled={disabled} />
            </label>
          </div>
          <label className="block">
            <span className="block text-v2-ink font-medium mb-1">Email <span className="text-v2-danger">*</span></span>
            <input type="email" value={email} onChange={(e) => onEmailChange(e.target.value)}
                   placeholder="alice@acme.com"
                   className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink text-[12px]" disabled={disabled} />
            <span className="text-[10.5px] text-v2-ink-muted">V1 invite: server creates a pending user + 24-hour token. The user activates via the email link.</span>
          </label>
        </>
      )}

      <label className="block">
        <span className="block text-v2-ink font-medium mb-1">Role</span>
        <select value={role} onChange={(e) => onRoleChange(e.target.value)}
                className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink text-[12px]" disabled={disabled}>
          {(availableRoles || ['Viewer', 'Developer', 'Tenant Admin', 'Instance Admin', 'Promoter', 'Org Admin']).map(r => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </label>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// V191 / G527 — orchestrateAdminAssign
//
// Shared helper that the Create-* slideouts call AFTER the resource is
// created. Handles both modes:
//   - existing: addUserMembership with the chosen user + scope
//   - invite:   createUser (V1 path) then addUserMembership for the
//               instance-scoped grant if instanceId is set (createUser
//               auto-grants tenant-wide only)
//
// Returns {ok:true} on full success, {ok:false, error:'...'} on any failure.
// Caller decides UX (toast warning + leave resource created).
// ──────────────────────────────────────────────────────────────────────
async function orchestrateAdminAssign({ state, orgId, tenantId, instanceId }) {
  if (!state.enabled) return { ok: true };
  const role = state.role || 'Viewer';

  if (state.mode === 'existing') {
    if (!state.pickedUser?.id) return { ok: false, error: 'Pick a user before submitting' };
    const res = await api.addUserMembership(state.pickedUser.id, {
      orgId, tenantId, instanceId, role, isDefault: false,
    });
    if (res && res.ok === false) return { ok: false, error: res.message || res.error || 'addUserMembership failed' };
    return { ok: true };
  }

  if (state.mode === 'invite') {
    if (!state.email?.trim()) return { ok: false, error: 'Email is required' };
    if (!state.firstName?.trim()) return { ok: false, error: 'First name is required' };
    // Route through the ONE invite pipeline (InvitationService.invite). It creates the invited
    // user at the TARGET org/tenant/instance, plants the membership THERE (not the caller's session
    // org — the reason the old code did an explicit follow-up grant), AND sends the invitation email
    // via the platform relay — then reports whether the email actually went out. This replaces the
    // old createUser + explicit addUserMembership pair, which planted the membership but never sent
    // an email, so the "invite sent" toast was cosmetic.
    const inviteRes = await api.sendInvitation({
      email: state.email.trim().toLowerCase(),
      firstName: state.firstName.trim(),
      lastName: (state.lastName || '').trim() || '',
      role,
      orgId: orgId || null,
      tenantId: tenantId || null,
      instanceId: instanceId || null,
    });
    if (!inviteRes || inviteRes.ok === false) {
      return { ok: false, error: inviteRes?.error || inviteRes?.data?.error || 'Invite failed' };
    }
    const data = (inviteRes && Object.prototype.hasOwnProperty.call(inviteRes, 'data'))
      ? (inviteRes.data || {}) : inviteRes;
    return {
      ok: true, invited: true, email: state.email,
      emailQueued: data.emailQueued, registrationUrl: data.registrationUrl,
    };
  }

  return { ok: true };
}

// ──────────────────────────────────────────────────────────────────────
// V191 / Phase-5b — Create Org Slideout.
//
// Fields: name (required), domain (optional).
// Calls api.createOrganization → POST /api/v1/organizations.
// Backend gates with org.create permission (Org Admin / Super Admin /
// Platform Admin). 403 on insufficient scope.
// ──────────────────────────────────────────────────────────────────────
function CreateOrgSlideout({ isOpen, onClose, customers, onCreated }) {
  const { addToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  // RC-92 — an org MUST land under a customer. listCustomers returns the full
  // ACTIVE list only for Platform Admin (all-customers); scoped callers get []
  // and are auto-pinned to their own customer server-side, so the picker only
  // renders (and is required) when we actually have customers to choose from.
  const [customerId, setCustomerId] = useState('');
  const activeCustomers = (Array.isArray(customers) ? customers : [])
    .filter((c) => (c.status ?? 'ACTIVE') === 'ACTIVE');
  const needsCustomer = activeCustomers.length > 0;
  // V194 / Phase-5b-fix — submit-race guard. setSubmitting(true) is
  // async (one React render cycle) so rapid double-clicks could fire
  // two API calls. The ref synchronously latches "in flight" and
  // short-circuits subsequent invocations until the response returns.
  const inFlight = useRef(false);
  // V191 / G527 — Admin assignment step state (default role: Org Admin).
  const [adminEnabled, setAdminEnabled] = useState(false);
  const [adminMode, setAdminMode] = useState('existing'); // existing | invite
  const [adminUserQuery, setAdminUserQuery] = useState('');
  const [adminPickedUser, setAdminPickedUser] = useState(null);
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminRole, setAdminRole] = useState('Org Admin');

  useEffect(() => {
    if (isOpen) {
      setFormError(null); setName(''); setDomain(''); setCustomerId('');
      inFlight.current = false;
      // Reset admin step
      setAdminEnabled(false); setAdminMode('existing');
      setAdminUserQuery(''); setAdminPickedUser(null);
      setAdminFirstName(''); setAdminLastName(''); setAdminEmail('');
      setAdminRole('Org Admin');
    }
  }, [isOpen]);

  const submit = async () => {
    if (inFlight.current) return;          // V194 race guard
    inFlight.current = true;
    setFormError(null);
    if (!name.trim()) { setFormError('Name is required'); inFlight.current = false; return; }
    if (needsCustomer && !customerId) { setFormError('Customer is required'); inFlight.current = false; return; }
    // V191 / G527 — Pre-flight admin step validation (avoid creating org
    // then failing the admin step due to a missing required field).
    if (adminEnabled) {
      if (adminMode === 'existing' && !adminPickedUser?.id) {
        setFormError('Pick a user for admin assignment, or uncheck "Assign admin now"');
        inFlight.current = false; return;
      }
      if (adminMode === 'invite') {
        if (!adminEmail.trim()) { setFormError('Admin email is required'); inFlight.current = false; return; }
        if (!adminFirstName.trim()) { setFormError('Admin first name is required'); inFlight.current = false; return; }
      }
    }
    setSubmitting(true);
    try {
      const res = await api.createOrganization({
        name: name.trim(),
        domain: domain.trim() || null,
        // RC-92 — stamp the owning customer (PA picks it; scoped callers omit it
        // and the backend pins to their cid). Never create an orphan org.
        customerId: customerId || undefined,
      });
      if (res && res.ok === false) {
        setFormError(res.message || res.error || 'Create failed');
        return;
      }
      const created = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
      // V191 / G527 — Resource created. Now run the admin assignment step.
      // Partial success policy: org stays created even if admin step fails;
      // we surface a warning toast directing the operator to the Team page.
      const adminRes = await orchestrateAdminAssign({
        state: {
          enabled: adminEnabled, mode: adminMode,
          pickedUser: adminPickedUser,
          firstName: adminFirstName, lastName: adminLastName, email: adminEmail,
          role: adminRole,
        },
        orgId: created?.id,
        tenantId: null,
        instanceId: null,
      });
      if (!adminRes.ok) {
        addToast({ tone: 'warning', title: 'Organization created — admin step failed',
                   message: `${created?.name} exists, but the admin assignment failed: ${adminRes.error}. Use the Team page to add the admin manually.` });
      } else if (adminEnabled) {
        addToast({ tone: 'success', title: 'Organization created',
                   message: adminRes.invited
                      ? (adminRes.emailQueued
                          ? `${created?.name} created and invite sent to ${adminRes.email}.`
                          : `${created?.name} created. ${adminRes.email} was added, but the invite email couldn't be sent — set up the platform email relay in Admin Center → Platform Connectors, then re-invite.`)
                      : `${created?.name} created and admin assigned.` });
      } else {
        addToast({ tone: 'success', title: 'Organization created',
                   message: `${created?.name || 'Organization'} created.` });
      }
      onCreated && onCreated(created);
      onClose();
    } catch (err) {
      setFormError(err?.message || String(err));
    } finally {
      setSubmitting(false);
      inFlight.current = false;
    }
  };

  return (
    <Slideout isOpen={isOpen} onClose={onClose} header="Create Organization"
              ariaLabel="Create Organization">
      <div className="space-y-4 p-1 text-[12.5px]">
        {formError && (
          <div className="text-[12px] text-v2-danger border border-v2-danger rounded-v2-md px-3 py-2 bg-v2-danger-soft">
            {formError}
          </div>
        )}
        {needsCustomer && (
          <label className="block">
            <span className="block text-v2-ink font-medium mb-1">Customer <span className="text-v2-danger">*</span></span>
            <select
              value={customerId} onChange={(e) => setCustomerId(e.target.value)}
              className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink"
              disabled={submitting}
            >
              <option value="">— select a Customer —</option>
              {activeCustomers.map((c) => (
                <option key={c.id} value={c.id}>{c.displayName || c.name}</option>
              ))}
            </select>
            <span className="text-[10.5px] text-v2-ink-muted">The organization is created under this customer.</span>
          </label>
        )}
        <label className="block">
          <span className="block text-v2-ink font-medium mb-1">Name <span className="text-v2-danger">*</span></span>
          <input
            type="text" value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Corp, Initech, …"
            maxLength={255}
            className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink"
            disabled={submitting}
          />
        </label>
        <label className="block">
          <span className="block text-v2-ink font-medium mb-1">Domain <span className="text-v2-ink-muted">(optional)</span></span>
          <input
            type="text" value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="acme.com"
            className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink"
            disabled={submitting}
          />
          <span className="text-[10.5px] text-v2-ink-muted">SSO / email auto-routing key.</span>
        </label>

        {/* V191 / G527 — Optional admin assignment step (Org Admin default). */}
        <AdminAssignStep
          enabled={adminEnabled} onEnabledChange={setAdminEnabled}
          mode={adminMode} onModeChange={setAdminMode}
          userQuery={adminUserQuery} onUserQueryChange={setAdminUserQuery}
          pickedUser={adminPickedUser} onPickedUserChange={setAdminPickedUser}
          firstName={adminFirstName} onFirstNameChange={setAdminFirstName}
          lastName={adminLastName} onLastNameChange={setAdminLastName}
          email={adminEmail} onEmailChange={setAdminEmail}
          role={adminRole} onRoleChange={setAdminRole}
          availableRoles={['Org Admin', 'Tenant Admin', 'Developer', 'Viewer']}
          disabled={submitting}
        />

        <div className="flex justify-end gap-2 pt-4 border-t border-v2-line">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={submitting || !name.trim() || (needsCustomer && !customerId)}>
            {submitting ? 'Creating…' : 'Create Organization'}
          </Button>
        </div>
      </div>
    </Slideout>
  );
}

// ──────────────────────────────────────────────────────────────────────
// V191 / Phase-5b — Create Tenant Slideout.
//
// Fields: orgId (picker, defaults to current selection), name (required).
// Calls api.createTenant → POST /api/v1/tenants.
// Backend gates with tenant.create permission + org-access check.
//
// V195 — the V1 tenants.environment column is deprecated; env semantics
// live on Instance.tier + Instance.is_production. The slideout no longer
// surfaces environment; backend normalizes the column to 'multi' on
// every create.
// ──────────────────────────────────────────────────────────────────────
function CreateTenantSlideout({ isOpen, onClose, orgs, customers, defaultOrgId, onCreated }) {
  const { addToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [orgId, setOrgId] = useState(defaultOrgId || '');
  const [name, setName] = useState('');
  const inFlight = useRef(false);                  // V194 race guard
  // RC-92 — Platform Admin must pick the Customer first; the Org picker then
  // lists ONLY that customer's orgs so a tenant can't be created under the
  // wrong customer. Scoped callers get [] customers and skip straight to orgs.
  const [customerId, setCustomerId] = useState('');
  const activeCustomers = (Array.isArray(customers) ? customers : [])
    .filter((c) => (c.status ?? 'ACTIVE') === 'ACTIVE');
  const needsCustomer = activeCustomers.length > 0;
  const orgOptions = (Array.isArray(orgs) ? orgs : []).filter(
    (o) => !needsCustomer || !customerId || (o.customerId || o.customer_id) === customerId,
  );
  // V191 / G527 — Admin assignment step state (default role: Tenant Admin).
  const [adminEnabled, setAdminEnabled] = useState(false);
  const [adminMode, setAdminMode] = useState('existing');
  const [adminUserQuery, setAdminUserQuery] = useState('');
  const [adminPickedUser, setAdminPickedUser] = useState(null);
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminRole, setAdminRole] = useState('Tenant Admin');

  useEffect(() => {
    if (isOpen) {
      setFormError(null);
      // Pre-select the customer from the tree-selected org (if any) so the org
      // picker is already scoped correctly.
      const defOrg = (Array.isArray(orgs) ? orgs : []).find((o) => o.id === defaultOrgId);
      setCustomerId(defOrg ? (defOrg.customerId || defOrg.customer_id || '') : '');
      setOrgId(defaultOrgId || '');
      setName('');
      inFlight.current = false;
      // Reset admin step
      setAdminEnabled(false); setAdminMode('existing');
      setAdminUserQuery(''); setAdminPickedUser(null);
      setAdminFirstName(''); setAdminLastName(''); setAdminEmail('');
      setAdminRole('Tenant Admin');
    }
  }, [isOpen, defaultOrgId]);

  const submit = async () => {
    if (inFlight.current) return;                  // V194 race guard
    inFlight.current = true;
    setFormError(null);
    if (needsCustomer && !customerId) { setFormError('Customer is required'); inFlight.current = false; return; }
    if (!orgId) { setFormError('Organization is required'); inFlight.current = false; return; }
    if (!name.trim()) { setFormError('Name is required'); inFlight.current = false; return; }
    // V191 / G527 — Pre-flight admin step validation.
    if (adminEnabled) {
      if (adminMode === 'existing' && !adminPickedUser?.id) {
        setFormError('Pick a user for admin assignment, or uncheck "Assign admin now"');
        inFlight.current = false; return;
      }
      if (adminMode === 'invite') {
        if (!adminEmail.trim()) { setFormError('Admin email is required'); inFlight.current = false; return; }
        if (!adminFirstName.trim()) { setFormError('Admin first name is required'); inFlight.current = false; return; }
      }
    }

    setSubmitting(true);
    try {
      const res = await api.createTenant({
        orgId,
        name: name.trim(),
        status: 'active',
      });
      if (res && res.ok === false) {
        setFormError(res.message || res.error || 'Create failed');
        return;
      }
      const created = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
      // V191 / G527 — Resource created. Run admin assignment scoped to the Tenant.
      const adminRes = await orchestrateAdminAssign({
        state: {
          enabled: adminEnabled, mode: adminMode,
          pickedUser: adminPickedUser,
          firstName: adminFirstName, lastName: adminLastName, email: adminEmail,
          role: adminRole,
        },
        orgId,
        tenantId: created?.id,
        instanceId: null,
      });
      if (!adminRes.ok) {
        addToast({ tone: 'warning', title: 'Tenant created — admin step failed',
                   message: `${created?.name} exists, but the admin assignment failed: ${adminRes.error}. Use the Team page to add the admin manually.` });
      } else if (adminEnabled) {
        addToast({ tone: 'success', title: 'Tenant created',
                   message: adminRes.invited
                      ? (adminRes.emailQueued
                          ? `${created?.name} created and invite sent to ${adminRes.email}.`
                          : `${created?.name} created. ${adminRes.email} was added, but the invite email couldn't be sent — set up the platform email relay in Admin Center → Platform Connectors, then re-invite.`)
                      : `${created?.name} created and admin assigned.` });
      } else {
        addToast({ tone: 'success', title: 'Tenant created',
                   message: `${created?.name || 'Tenant'} created.` });
      }
      onCreated && onCreated(created);
      onClose();
    } catch (err) {
      setFormError(err?.message || String(err));
    } finally {
      setSubmitting(false);
      inFlight.current = false;
    }
  };

  return (
    <Slideout isOpen={isOpen} onClose={onClose} header="Create Tenant"
              ariaLabel="Create Tenant">
      <div className="space-y-4 p-1 text-[12.5px]">
        {formError && (
          <div className="text-[12px] text-v2-danger border border-v2-danger rounded-v2-md px-3 py-2 bg-v2-danger-soft">
            {formError}
          </div>
        )}
        {needsCustomer && (
          <label className="block">
            <span className="block text-v2-ink font-medium mb-1">Customer <span className="text-v2-danger">*</span></span>
            <select
              value={customerId}
              onChange={(e) => { setCustomerId(e.target.value); setOrgId(''); }}
              className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink"
              disabled={submitting}
            >
              <option value="">— select a Customer —</option>
              {activeCustomers.map((c) => (
                <option key={c.id} value={c.id}>{c.displayName || c.name}</option>
              ))}
            </select>
            <span className="text-[10.5px] text-v2-ink-muted">Pick the customer; the Organization list below is scoped to it.</span>
          </label>
        )}
        <label className="block">
          <span className="block text-v2-ink font-medium mb-1">Organization <span className="text-v2-danger">*</span></span>
          <select
            value={orgId} onChange={(e) => setOrgId(e.target.value)}
            className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink"
            disabled={submitting || (needsCustomer && !customerId)}
          >
            <option value="">{needsCustomer && !customerId ? '— select a Customer first —' : '— select an Organization —'}</option>
            {orgOptions.map(o => (
              <option key={o.id} value={o.id}>
                {o.name} ({o.id.slice(0, 8)}…)
              </option>
            ))}
          </select>
          <span className="text-[10.5px] text-v2-ink-muted">A tenant is a business unit within an organization.</span>
        </label>
        <label className="block">
          <span className="block text-v2-ink font-medium mb-1">Name <span className="text-v2-danger">*</span></span>
          <input
            type="text" value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Healthcare, Acme Finance, …"
            maxLength={255}
            className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink"
            disabled={submitting}
          />
          <span className="text-[10.5px] text-v2-ink-muted">Unique within the Organization. Env semantics live on Instances under this Tenant.</span>
        </label>

        {/* V191 / G527 — Optional admin assignment step (Tenant Admin default). */}
        <AdminAssignStep
          enabled={adminEnabled} onEnabledChange={setAdminEnabled}
          mode={adminMode} onModeChange={setAdminMode}
          userQuery={adminUserQuery} onUserQueryChange={setAdminUserQuery}
          pickedUser={adminPickedUser} onPickedUserChange={setAdminPickedUser}
          firstName={adminFirstName} onFirstNameChange={setAdminFirstName}
          lastName={adminLastName} onLastNameChange={setAdminLastName}
          email={adminEmail} onEmailChange={setAdminEmail}
          role={adminRole} onRoleChange={setAdminRole}
          availableRoles={['Tenant Admin', 'Promoter', 'Developer', 'Viewer']}
          disabled={submitting}
        />

        <div className="flex justify-end gap-2 pt-4 border-t border-v2-line">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={submitting || !orgId || !name.trim() || (needsCustomer && !customerId)}>
            {submitting ? 'Creating…' : 'Create Tenant'}
          </Button>
        </div>
      </div>
    </Slideout>
  );
}

// ──────────────────────────────────────────────────────────────────────
// V191 / Phase-5b — Create Instance Slideout. (G527 — admin step upgraded)
//
// Captures the V191 schema fields the caller may set on create:
//   - tenantId (picker, defaults to current selection)
//   - name (required, max 80, unique within tenant)
//   - tier (select: PRODUCTION / STAGING / DEV / SYSTEM / CUSTOM)
//   - isProduction (boolean)
//   - requiresPromotionApproval (boolean)
//   - approverRole (free-form, optional)
//   - description (textarea, optional)
//   - Optional admin assignment via shared <AdminAssignStep>:
//       Mode A — Existing user (search /api/v1/team/users + pick)
//       Mode B — Invite by email (V1 path POST /api/v1/team/users
//                with status='invited' → auto-generates 24-hour token +
//                tenant-wide UserOrgRole grant + a 2nd Instance-scoped
//                grant via api.addUserMembership)
//       Default role: "Instance Admin"
//
// All gated by backend Phase-3 RBAC (instance.create at tenant scope).
// Failure paths surface to toast + inline form error. Admin-step failure
// AFTER successful instance create is non-fatal — instance stays, warning
// toast directs operator to the Team page to retry the grant manually.
// ──────────────────────────────────────────────────────────────────────
function CreateInstanceSlideout({ isOpen, onClose, tenants, orgs, customers, defaultTenantId, onCreated }) {
  const { addToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [tenantId, setTenantId] = useState(defaultTenantId || '');
  // RC-92 — Platform Admin picks the Customer first; the Tenant picker then
  // lists only that customer's tenants (tenant → org.customerId). Scoped callers
  // get [] customers and pick a tenant directly from their own scope.
  const [customerId, setCustomerId] = useState('');
  const [name, setName] = useState('');
  const [tier, setTier] = useState('CUSTOM');
  const [isProduction, setIsProduction] = useState(false);
  const [requiresPromotionApproval, setRequiresPromotionApproval] = useState(false);
  const [approverRole, setApproverRole] = useState('');
  const [description, setDescription] = useState('');
  // V191 / G527 — Replace raw-UUID admin input with full AdminAssignStep
  // (existing user search OR invite by email). Default role: Instance Admin.
  const [adminEnabled, setAdminEnabled] = useState(false);
  const [adminMode, setAdminMode] = useState('existing');
  const [adminUserQuery, setAdminUserQuery] = useState('');
  const [adminPickedUser, setAdminPickedUser] = useState(null);
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminRole, setAdminRole] = useState('Instance Admin');
  const inFlight = useRef(false);                      // V194 race guard

  // Reset on open.
  useEffect(() => {
    if (isOpen) {
      setFormError(null);
      // Pre-select the customer from the tree-selected tenant (tenant→org→customer)
      // so the tenant picker is already scoped.
      const defTenant = (Array.isArray(tenants) ? tenants : []).find((t) => t.id === defaultTenantId);
      const defOrg = defTenant ? (Array.isArray(orgs) ? orgs : []).find((o) => o.id === defTenant.orgId) : null;
      setCustomerId(defOrg ? (defOrg.customerId || defOrg.customer_id || '') : '');
      setTenantId(defaultTenantId || '');
      setName('');
      setTier('CUSTOM');
      setIsProduction(false);
      setRequiresPromotionApproval(false);
      setApproverRole('');
      setDescription('');
      // Reset admin step
      setAdminEnabled(false); setAdminMode('existing');
      setAdminUserQuery(''); setAdminPickedUser(null);
      setAdminFirstName(''); setAdminLastName(''); setAdminEmail('');
      setAdminRole('Instance Admin');
      inFlight.current = false;
    }
  }, [isOpen, defaultTenantId]);

  const submit = async () => {
    if (inFlight.current) return;                      // V194 race guard
    inFlight.current = true;
    setFormError(null);
    const fail = (msg) => { setFormError(msg); inFlight.current = false; };
    if (needsCustomer && !customerId) { fail('Customer is required'); return; }
    if (!tenantId) { fail('Tenant is required'); return; }
    if (!name.trim()) { fail('Name is required'); return; }
    if (name.trim().length > 80) { fail('Name must be 80 chars or fewer'); return; }
    // V191 / G527 — Pre-flight admin step validation.
    if (adminEnabled) {
      if (adminMode === 'existing' && !adminPickedUser?.id) {
        fail('Pick a user for admin assignment, or uncheck "Assign admin now"'); return;
      }
      if (adminMode === 'invite') {
        if (!adminEmail.trim()) { fail('Admin email is required'); return; }
        if (!adminFirstName.trim()) { fail('Admin first name is required'); return; }
      }
    }

    // V191 / G527 — The instance creation payload no longer carries
    // assignAdminUserId / adminRole. Admin assignment is now a separate
    // orchestrated step (post-create) so we can reuse the V1 invite path
    // for new users — backend createInstance only knows about user-ids.
    const body = {
      tenantId,
      name: name.trim(),
      tier,
      isProduction,
      requiresPromotionApproval,
      approverRole: approverRole.trim() || null,
      description: description.trim() || null,
    };

    setSubmitting(true);
    try {
      const res = await api.createInstance(body);
      if (res && res.ok === false) {
        // apiFetch surfaces 409 as {ok:false, status:409, duplicate:true,
        // error:'Conflict', message:'Instance X already exists ...'}.
        // Prefer the precise message; fall back to error then to generic.
        setFormError(res.message || res.error || 'Create failed');
        return;
      }
      const created = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
      // V191 / G527 — Resolve parent orgId from tenants list (admin grant
      // needs orgId for tenant-wide auto-grant; instanceId pins the row).
      const parentTenant = (tenants || []).find(t => t.id === tenantId);
      const adminRes = await orchestrateAdminAssign({
        state: {
          enabled: adminEnabled, mode: adminMode,
          pickedUser: adminPickedUser,
          firstName: adminFirstName, lastName: adminLastName, email: adminEmail,
          role: adminRole,
        },
        orgId: parentTenant?.orgId || null,
        tenantId,
        instanceId: created?.id,
      });
      if (!adminRes.ok) {
        addToast({ tone: 'warning', title: 'Instance created — admin step failed',
                   message: `${created?.name} (${created?.tier}) exists, but the admin assignment failed: ${adminRes.error}. Use the Team page to add the admin manually.` });
      } else if (adminEnabled) {
        addToast({ tone: 'success', title: 'Instance created',
                   message: adminRes.invited
                      ? (adminRes.emailQueued
                          ? `${created?.name} (${created?.tier}) created and invite sent to ${adminRes.email}.`
                          : `${created?.name} (${created?.tier}) created. ${adminRes.email} was added, but the invite email couldn't be sent — set up the platform email relay in Admin Center → Platform Connectors, then re-invite.`)
                      : `${created?.name} (${created?.tier}) created and admin assigned.` });
      } else {
        addToast({ tone: 'success', title: 'Instance created',
                   message: `${created?.name || 'Instance'} (${created?.tier || 'CUSTOM'}) created.` });
      }
      onCreated && onCreated(created);
      onClose();
    } catch (err) {
      setFormError(err?.message || String(err));
    } finally {
      setSubmitting(false);
      inFlight.current = false;
    }
  };

  // RC-92 — Customer-scoped tenant list. listCustomers is populated only for
  // Platform Admin (all-customers); scoped callers get [] and pick from their
  // own tenants directly. tenant → owning org → customer lets us filter.
  const activeCustomers = (Array.isArray(customers) ? customers : [])
    .filter((c) => (c.status ?? 'ACTIVE') === 'ACTIVE');
  const needsCustomer = activeCustomers.length > 0;
  const orgCustomerById = {};
  for (const o of (Array.isArray(orgs) ? orgs : [])) {
    orgCustomerById[o.id] = o.customerId || o.customer_id || null;
  }
  const tenantOptions = (Array.isArray(tenants) ? tenants : []).filter(
    (t) => !needsCustomer || !customerId || orgCustomerById[t.orgId] === customerId,
  );

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      header="Create Instance"
      ariaLabel="Create Instance"
    >
      <div className="space-y-4 p-1 text-[12.5px]">
        {formError && (
          <div className="text-[12px] text-v2-danger border border-v2-danger rounded-v2-md px-3 py-2 bg-v2-danger-soft">
            {formError}
          </div>
        )}

        {/* Customer picker (Platform Admin) — scopes the Tenant list below */}
        {needsCustomer && (
          <label className="block">
            <span className="block text-v2-ink font-medium mb-1">Customer <span className="text-v2-danger">*</span></span>
            <select
              value={customerId}
              onChange={(e) => { setCustomerId(e.target.value); setTenantId(''); }}
              className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink"
              disabled={submitting}
            >
              <option value="">— select a Customer —</option>
              {activeCustomers.map((c) => (
                <option key={c.id} value={c.id}>{c.displayName || c.name}</option>
              ))}
            </select>
            <span className="text-[10.5px] text-v2-ink-muted">Pick the customer; the Tenant list below is scoped to it.</span>
          </label>
        )}

        {/* Tenant picker */}
        <label className="block">
          <span className="block text-v2-ink font-medium mb-1">Tenant <span className="text-v2-danger">*</span></span>
          <select
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink"
            disabled={submitting || (needsCustomer && !customerId)}
          >
            <option value="">{needsCustomer && !customerId ? '— select a Customer first —' : '— select a Tenant —'}</option>
            {tenantOptions.map(t => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.id.slice(0, 8)}…)
              </option>
            ))}
          </select>
        </label>

        {/* Name */}
        <label className="block">
          <span className="block text-v2-ink font-medium mb-1">Name <span className="text-v2-danger">*</span></span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Production, Staging, Dev, …"
            maxLength={80}
            className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink"
            disabled={submitting}
          />
          <span className="text-[10.5px] text-v2-ink-muted">Unique within the Tenant. Max 80 chars.</span>
        </label>

        {/* Tier */}
        <label className="block">
          <span className="block text-v2-ink font-medium mb-1">Tier</span>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink"
            disabled={submitting}
          >
            <option value="PRODUCTION">PRODUCTION</option>
            <option value="STAGING">STAGING</option>
            <option value="DEV">DEV</option>
            <option value="SYSTEM">SYSTEM</option>
            <option value="CUSTOM">CUSTOM</option>
          </select>
        </label>

        {/* is_production + requires_promotion_approval */}
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-v2-ink">
            <input type="checkbox" checked={isProduction}
                   onChange={(e) => setIsProduction(e.target.checked)}
                   disabled={submitting} />
            <span>Is production</span>
          </label>
          <label className="flex items-center gap-2 text-v2-ink">
            <input type="checkbox" checked={requiresPromotionApproval}
                   onChange={(e) => setRequiresPromotionApproval(e.target.checked)}
                   disabled={submitting} />
            <span>Requires promotion approval</span>
          </label>
        </div>

        {/* Approver role */}
        <label className="block">
          <span className="block text-v2-ink font-medium mb-1">Approver role <span className="text-v2-ink-muted">(optional)</span></span>
          <input
            type="text"
            value={approverRole}
            onChange={(e) => setApproverRole(e.target.value)}
            placeholder="e.g. Org Admin, Tenant Admin"
            className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink"
            disabled={submitting}
          />
          <span className="text-[10.5px] text-v2-ink-muted">Role required to approve promotions targeting this Instance. Leave blank for "any user with promotion.approve".</span>
        </label>

        {/* Description */}
        <label className="block">
          <span className="block text-v2-ink font-medium mb-1">Description <span className="text-v2-ink-muted">(optional)</span></span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full border border-v2-line rounded-v2-md px-3 py-2 bg-white text-v2-ink"
            disabled={submitting}
          />
        </label>

        {/* V191 / G527 — Optional admin assignment step (Instance Admin default).
            Replaces the raw-UUID input from Phase-5b with the unified
            existing-user-or-invite-by-email picker used across all 3
            Create slideouts. Reuses V1 invite path via orchestrateAdminAssign. */}
        <AdminAssignStep
          enabled={adminEnabled} onEnabledChange={setAdminEnabled}
          mode={adminMode} onModeChange={setAdminMode}
          userQuery={adminUserQuery} onUserQueryChange={setAdminUserQuery}
          pickedUser={adminPickedUser} onPickedUserChange={setAdminPickedUser}
          firstName={adminFirstName} onFirstNameChange={setAdminFirstName}
          lastName={adminLastName} onLastNameChange={setAdminLastName}
          email={adminEmail} onEmailChange={setAdminEmail}
          role={adminRole} onRoleChange={setAdminRole}
          availableRoles={['Instance Admin', 'Tenant Admin', 'Promoter', 'Developer', 'Viewer']}
          disabled={submitting}
        />

        <div className="flex justify-end gap-2 pt-4 border-t border-v2-line">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={submitting || !tenantId || !name.trim() || (needsCustomer && !customerId)}>
            {submitting ? 'Creating…' : 'Create Instance'}
          </Button>
        </div>
      </div>
    </Slideout>
  );
}

export default function MultiTenancyPageV2() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { addToast } = useToast();
  const { session, hasPermission } = useAuth();
  const platform = usePlatform() || {};

  // Defensive: destructure defaults only fire on `=== undefined`. Some
  // platform loaders set state to null on failure paths, so we coerce.
  const orgs = Array.isArray(platform.orgs) ? platform.orgs : [];
  const tenants = Array.isArray(platform.tenants) ? platform.tenants : [];
  const { loadOrganizations, loadTenants } = platform;

  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* V208 tier-1 surface: load customers so the tree can group orgs
   * beneath their owning customer header (instead of presenting orgs as
   * if they were top-level entities — which they aren't, post-V208).
   * Platform Admin gets the full customer list; other roles get [] and
   * the tree falls back to flat-orgs rendering. */
  const [customers, setCustomers] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (typeof api.listCustomers !== 'function') return;
    api.listCustomers()
      .then((res) => {
        if (cancelled) return;
        if (res && res.ok === false) { setCustomers([]); return; }
        const list = (res && 'data' in res) ? res.data : res;
        setCustomers(Array.isArray(list) ? list : []);
      })
      .catch(() => { if (!cancelled) setCustomers([]); });
    return () => { cancelled = true; };
  }, []);

  // V191 / Phase-5b — create-flow state.
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [createTenantOpen, setCreateTenantOpen] = useState(false);
  const [createInstanceOpen, setCreateInstanceOpen] = useState(false);

  // V191 / Phase-5b-fix (G523) — edit + delete state. Pointer to the
  // row being edited / confirmed for delete + which dialog is open.
  const [editOrg, setEditOrg] = useState(null);
  const [editTenant, setEditTenant] = useState(null);
  const [editInstance, setEditInstance] = useState(null);
  const [deleteOrg, setDeleteOrg] = useState(null);
  const [deleteTenant, setDeleteTenant] = useState(null);
  const [archiveInstance, setArchiveInstance] = useState(null);

  const reloadInstances = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Pre-existing api.getInstances() hits the same GET /api/v1/instances
      // endpoint the Phase-3 MultiTenancyController serves; backend filters
      // by the caller's most-specific-wins effective role.
      //
      // G543 — DO NOT forward the URL ?orgId= here. Earlier (G526) we
      // forwarded the URL orgId to enable cross-org SuperAdmin browsing,
      // but the backend's default behavior already returns every Instance
      // visible to the caller's RBAC scope (active org for normal roles;
      // platform-wide for Super Admin / Platform Admin). The forwarded
      // override narrowed the result to ONE org, so every OTHER tenant in
      // the tree displayed "0 instances" — a visible regression every time
      // the operator selected anything under a different org. Backend
      // RBAC scope is the source of truth; URL ?orgId= is purely a
      // selection hint for the tree + detail panel, not a data filter.
      const res = await api.getInstances();
      if (res && res.ok === false) {
        const msg = res.error || 'Failed to load Instances';
        setError(msg);
        addToast({ tone: 'danger', title: 'Failed to load Instances', message: msg });
        setInstances([]);
        return;
      }
      const payload = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
      setInstances(Array.isArray(payload) ? payload : []);
    } catch (err) {
      console.error('getInstances failed', err);
      setError(err?.message || 'Failed to load Instances');
      addToast({ tone: 'danger', title: 'Failed to load Instances', message: err?.message || String(err) });
    } finally {
      setLoading(false);
    }
  }, [addToast]);                       // G543 — no URL-orgId dep; load is scope-agnostic


  const reloadAll = useCallback(() => {
    if (typeof loadOrganizations === 'function') loadOrganizations();
    if (typeof loadTenants === 'function') loadTenants();
    reloadInstances();
  }, [loadOrganizations, loadTenants, reloadInstances]);

  useEffect(() => { reloadAll(); }, [reloadAll]);

  // Group Instances by tenant for the tree.
  const instancesByTenantId = useMemo(() => {
    const map = {};
    for (const i of instances) {
      if (!map[i.tenantId]) map[i.tenantId] = [];
      map[i.tenantId].push(i);
    }
    Object.values(map).forEach(arr => arr.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    return map;
  }, [instances]);

  // Selection — driven by URL params so the page is deep-linkable.
  const selected = useMemo(() => {
    const orgId = params.get('orgId');
    const tenantId = params.get('tenantId');
    const instanceId = params.get('instanceId');
    if (instanceId) return { kind: 'instance', orgId, tenantId, instanceId };
    if (tenantId) return { kind: 'tenant', orgId, tenantId };
    if (orgId) return { kind: 'org', orgId };
    // Default: pick caller's session org / tenant.
    if (session?.orgId) {
      return session.tenantId
        ? { kind: 'tenant', orgId: session.orgId, tenantId: session.tenantId }
        : { kind: 'org', orgId: session.orgId };
    }
    return null;
  }, [params, session]);

  const onSelect = useCallback((sel) => {
    const next = new URLSearchParams();
    if (sel.orgId) next.set('orgId', sel.orgId);
    if (sel.tenantId) next.set('tenantId', sel.tenantId);
    if (sel.instanceId) next.set('instanceId', sel.instanceId);
    setParams(next, { replace: true });
  }, [setParams]);

  // Resolve the row to display in the detail panel.
  const detail = useMemo(() => {
    if (!selected) return null;
    if (selected.kind === 'org') {
      const org = orgs.find(o => o.id === selected.orgId);
      return org ? { kind: 'org', org } : null;
    }
    if (selected.kind === 'tenant') {
      const tenant = tenants.find(t => t.id === selected.tenantId);
      return tenant ? { kind: 'tenant', tenant, instances: instancesByTenantId[tenant.id] || [] } : null;
    }
    if (selected.kind === 'instance') {
      const instance = instances.find(i => i.id === selected.instanceId);
      return instance ? { kind: 'instance', instance } : null;
    }
    return null;
  }, [selected, orgs, tenants, instances, instancesByTenantId]);

  return (
    <div className="p-6">
      {/* ─── Header ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-[20px] font-semibold text-v2-ink flex items-center gap-2">
            <Building2 size={20} className="text-v2-ink-muted" />
            Multi-Tenancy
          </h1>
          <p className="text-[12px] text-v2-ink-muted mt-1">
            Manage organizations, tenants and instances. Assign administrators and protect access at each level.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reloadAll}>
            Refresh
          </Button>
          {hasPermission('org.create') && (
            <Button
              variant="secondary" size="sm" iconLeft={<Plus size={13} />}
              onClick={() => setCreateOrgOpen(true)}
              title="Create a new Organization (requires org.create)"
            >
              New Org
            </Button>
          )}
          {hasPermission('tenant.create') && (
            <Button
              variant="secondary" size="sm" iconLeft={<Plus size={13} />}
              onClick={() => setCreateTenantOpen(true)}
              title="Create a new Tenant under an Organization (requires tenant.create)"
            >
              New Tenant
            </Button>
          )}
          {hasPermission('instance.create') && (
            <Button
              variant="primary" size="sm" iconLeft={<Plus size={13} />}
              onClick={() => setCreateInstanceOpen(true)}
            >
              New Instance
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 text-[12px] text-v2-danger border border-v2-danger rounded-v2-md px-3 py-2 bg-v2-danger-soft">
          {error}
        </div>
      )}

      {/* ─── Main grid ───────────────────────────────────────────── */}
      <div className="grid gap-5" style={{ gridTemplateColumns: '300px 1fr' }}>
        <TenancyTree
          customers={customers}
          orgs={orgs}
          tenants={tenants}
          instancesByTenantId={instancesByTenantId}
          selected={selected}
          onSelect={onSelect}
        />

        <div>
          {loading && instances.length === 0 ? (
            <Card><Card.Body>
              <div className="text-v2-ink-muted text-[12px]">Loading instances…</div>
            </Card.Body></Card>
          ) : !detail ? (
            <EmptyState
              icon={<Building2 size={20} />}
              title="Pick an Org, Tenant or Instance"
              description="Use the tree on the left to drill into your tenancy."
            />
          ) : detail.kind === 'org' ? (
            <OrgDetailPanel org={detail.org} tenants={tenants}
                            onEdit={setEditOrg} onDelete={setDeleteOrg}
                            canEdit={hasPermission('org.edit')}
                            canDelete={hasPermission('org.delete')} />
          ) : detail.kind === 'tenant' ? (
            <TenantDetailPanel tenant={detail.tenant} instances={detail.instances}
                               onEdit={setEditTenant} onDelete={setDeleteTenant}
                               canEdit={hasPermission('tenant.edit')}
                               canDelete={hasPermission('tenant.delete')} />
          ) : (
            <InstanceDetailPanel instance={detail.instance}
                                 onEdit={setEditInstance} onArchive={setArchiveInstance}
                                 canEdit={hasPermission('instance.edit')}
                                 canArchive={hasPermission('instance.delete')} />
          )}
        </div>
      </div>

      {/* V191 / Phase-5b — Create slideouts. Mounted always; open state
          driven by the three header buttons. Each slideout re-fetches
          the relevant list on success so the tree reflects the new row
          immediately without a full page reload. */}
      <CreateOrgSlideout
        isOpen={createOrgOpen}
        onClose={() => setCreateOrgOpen(false)}
        customers={customers}
        onCreated={() => { if (loadOrganizations) loadOrganizations(); }}
      />
      <CreateTenantSlideout
        isOpen={createTenantOpen}
        onClose={() => setCreateTenantOpen(false)}
        orgs={orgs}
        customers={customers}
        defaultOrgId={selected?.orgId || session?.orgId || ''}
        onCreated={() => { if (loadTenants) loadTenants(); }}
      />
      <CreateInstanceSlideout
        isOpen={createInstanceOpen}
        onClose={() => setCreateInstanceOpen(false)}
        tenants={tenants}
        orgs={orgs}
        customers={customers}
        defaultTenantId={selected?.tenantId || session?.tenantId || ''}
        onCreated={() => reloadInstances()}
      />

      {/* V191 / Phase-5b-fix (G523) — Edit slideouts + destructive confirm dialogs. */}
      <EditOrgSlideout
        isOpen={!!editOrg}
        onClose={() => setEditOrg(null)}
        org={editOrg}
        onSaved={() => { if (loadOrganizations) loadOrganizations(); }}
      />
      <EditTenantSlideout
        isOpen={!!editTenant}
        onClose={() => setEditTenant(null)}
        tenant={editTenant}
        onSaved={() => { if (loadTenants) loadTenants(); }}
      />
      <EditInstanceSlideout
        isOpen={!!editInstance}
        onClose={() => setEditInstance(null)}
        instance={editInstance}
        onSaved={() => reloadInstances()}
      />

      <ConfirmDeleteDialog
        isOpen={!!deleteOrg}
        onClose={() => setDeleteOrg(null)}
        kind="Organization"
        name={deleteOrg?.name}
        isSoft={false}
        onConfirm={async () => {
          const res = await api.deleteOrganization(deleteOrg.id);
          if (res?.ok !== false) {
            addToast({ tone: 'success', title: 'Organization deleted',
                       message: `${deleteOrg.name} removed (cascade applied).` });
            // G533 — clear URL selection BEFORE refetching so reloadInstances
            // doesn't pass the deleted org's id back to the backend (which
            // would 404 or "cannot list outside your Org" the next loader).
            // Resets the page to the empty-state "Pick an Org, Tenant or
            // Instance" prompt, which is the right post-cascade view.
            setParams(new URLSearchParams(), { replace: true });
            if (loadOrganizations) loadOrganizations();
            if (loadTenants) loadTenants();
            reloadInstances();
          }
          return res;
        }}
      />
      <ConfirmDeleteDialog
        isOpen={!!deleteTenant}
        onClose={() => setDeleteTenant(null)}
        kind="Tenant"
        name={deleteTenant?.name}
        isSoft={false}
        onConfirm={async () => {
          const res = await api.deleteTenant(deleteTenant.id);
          if (res?.ok !== false) {
            addToast({ tone: 'success', title: 'Tenant deleted',
                       message: `${deleteTenant.name} removed.` });
            // G533 — keep ?orgId= so the operator stays inside the parent
            // org's subtree; drop ?tenantId= and ?instanceId= so they
            // don't point at deleted rows on the next reload.
            const next = new URLSearchParams();
            const keepOrgId = params.get('orgId');
            if (keepOrgId) next.set('orgId', keepOrgId);
            setParams(next, { replace: true });
            if (loadTenants) loadTenants();
            reloadInstances();
          }
          return res;
        }}
      />
      <ConfirmDeleteDialog
        isOpen={!!archiveInstance}
        onClose={() => setArchiveInstance(null)}
        kind="Instance"
        name={archiveInstance?.name}
        isSoft={true}
        onConfirm={async () => {
          const res = await api.deleteInstance(archiveInstance.id);
          if (res?.ok !== false) {
            addToast({ tone: 'success', title: 'Instance archived',
                       message: `${archiveInstance.name} → status=ARCHIVED. Reversible via Edit.` });
            // G533 — Instance is soft-archived (status=ARCHIVED), not
            // hard-deleted. Keep ?orgId=&tenantId= so the operator returns
            // to the parent tenant view; just drop the archived
            // ?instanceId= from the selection.
            const next = new URLSearchParams();
            const keepOrgId = params.get('orgId');
            const keepTenantId = params.get('tenantId');
            if (keepOrgId) next.set('orgId', keepOrgId);
            if (keepTenantId) next.set('tenantId', keepTenantId);
            setParams(next, { replace: true });
            reloadInstances();
          }
          return res;
        }}
      />
    </div>
  );
}
